'use strict';
// ============================================================================
// YARN ORDER FORM - MODULAR ARCHITECTURE
// ============================================================================

const LATEST_DATA_VERSION = 1;

// ============================================================================
// STATE MODULE - Single source of truth
// ============================================================================
const State = (function() {
    let state = {
        dataVersion: LATEST_DATA_VERSION,
        orders: [],
        orderCounter: 1,
        changeLog: [],
        currentMode: 'seller', // 'client' or 'seller'
        clientSession: {
            email: null,
            currentOrderId: null,
            orderItems: [],
        },
        currency: {
            exchangeRateMinor: null,
            lastUpdate: null,
            lastReminderMonth: null,
            lastRateConfirmAt: null,  // timestamp последнего подтверждения актуальности
            rateSource: null,  // источник курса
        },
        // ✅ NEW: Справочник цен на пряжу (цена в копейках за грамм)
        yarnPrices: {
            'feodosiia-230': 1600,           // 16.00 ₽/г
            'feodosiia-350': 1600,           // 16.00 ₽/г
            'feodosiia-cloud-350': 2000,     // 20.00 ₽/г
            'feodosiia-cloud-175': 2000,     // 20.00 ₽/г
            'todd-duncan-cashmere': 4000,    // 40.00 ₽/г
        },
        notifications: [],
    };

    return {
        get: () => ({ ...state }),
        getOrders: () => [...state.orders],
        getCurrency: () => ({ ...state.currency }),
        getClientSession: () => ({ ...state.clientSession }),
        getYarnPrice: (yarnId) => state.yarnPrices[yarnId] || 0,  // ✅ NEW: Get price per gram

        setState: (newState) => {
            state = { ...state, ...newState };
            Storage.save();
        },

        updateOrders: (orders) => {
            state.orders = orders;
            Storage.save();
        },

        updateCurrency: (currency) => {
            state.currency = { ...state.currency, ...currency };
            Storage.save();
        },

        addOrder: (order) => {
            state.orders.unshift(order);
            state.orderCounter++;
            Storage.save();
        },

        addChangeLog: (log) => {
            state.changeLog.unshift(log);
            Storage.save();
        },

        reset: () => {
            state = {
                dataVersion: LATEST_DATA_VERSION,
                orders: [],
                orderCounter: 1,
                changeLog: [],
                currentMode: 'seller',
                clientSession: { email: null, currentOrderId: null, orderItems: [] },
                currency: { 
                    exchangeRateMinor: null, 
                    lastUpdate: null, 
                    lastReminderMonth: null,
                    lastRateConfirmAt: null,
                    rateSource: null
                },
                yarnPrices: {
                    'feodosiia-230': 1600,
                    'feodosiia-350': 1600,
                    'feodosiia-cloud-350': 2000,
                    'feodosiia-cloud-175': 2000,
                    'todd-duncan-cashmere': 4000,
                },
                notifications: [],
            };
        },

        loadState: (loadedState) => {
            state = { ...state, ...loadedState };
        }
    };
})();

// ============================================================================
// STORAGE MODULE - localStorage management
// ============================================================================
const Storage = (function() {
    const STORAGE_KEY = 'yarnOrderFormState';

    function save() {
        try {
            const state = State.get();
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            console.log('💾 State saved to localStorage');
        } catch (e) {
            console.error('Storage save error:', e);
            UI.showAlert('⚠️ Ошибка сохранения данных! Возможно, переполнено хранилище браузера.');
        }
    }

    function load() {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const state = JSON.parse(saved);
                // Migrate if needed
                const migrated = migrate(state);
                State.loadState(migrated);
                console.log('✅ State loaded from localStorage');
                return true;
            }
        } catch (e) {
            console.error('Storage load error:', e);
        }
        return false;
    }

    function migrate(state) {
        // ========== IDEMPOTENT MIGRATIONS ==========
        // Safe to run multiple times - checks if migration already applied

        if (!state.dataVersion) {
            state.dataVersion = LATEST_DATA_VERSION;
        }

        // Ensure all required fields exist
        if (!state.currency) state.currency = {};
        if (!state.notifications) state.notifications = [];

        // ========== Migration logic: ==========
        // there is no migration logic yet

        // ========== Add yarnPrices if missing ==========
        if (!state.yarnPrices) {
            console.log('🔄 Adding yarn prices catalog...');
            state.yarnPrices = {
                'feodosiia-230': 1600,
                'feodosiia-350': 1600,
                'feodosiia-cloud-350': 2000,
                'feodosiia-cloud-175': 2000,
                'todd-duncan-cashmere': 4000
            };
            console.log('✅ Yarn prices added');
        }
        
        return state;
    }
    
    return {
        save,
        load
    };
})();

// ============================================================================
// PRICING MODULE - Calculations and currency conversion
// All monetary values are stored in minor units (kopecks/pence)
// 1 RUB = 100 kopecks, 1 GBP = 100 pence
// ============================================================================
const Pricing = (function() {
    // ========== Utilities ==========
    
    /**
     * Convert rubles to kopecks (minor units)
     * @param {number} value - Amount in rubles
     * @returns {number} - Amount in kopecks (integer)
     */
    const toMinor = (value) => Math.round(Number(value) * 100) || 0;
    
    /**
     * Convert kopecks to rubles (major units)
     * @param {number} kopecks - Amount in kopecks
     * @returns {string} - Formatted amount in rubles (2 decimal places)
     */
    const fromMinor = (kopecks) => (kopecks / 100).toFixed(2);
    
    /**
     * Format minor units for display with currency symbol
     * @param {number} kopecks - Amount in kopecks
     * @param {string} currency - Currency symbol (₽ or £)
     * @returns {string} - Formatted price
     */
    const formatMinor = (kopecks, currency = '₽') => {
        return `${fromMinor(kopecks)} ${currency}`;
    };
    
    // ========== Delivery Rates (in kopecks) ==========
    
    const DELIVERY_RATES = [
        { max: 50, price: 147000 },      // 50г = 1470₽
        { max: 100, price: 142000 },     // 100г = 1420₽
        { max: 150, price: 137000 },     // 150г = 1370₽
        { max: 200, price: 132000 },     // 200г = 1320₽
        { max: 250, price: 127000 },     // 250г = 1270₽
        { max: 300, price: 122000 },     // 300г = 1220₽
        { max: 350, price: 117000 },     // 350г = 1170₽
        { max: 400, price: 112000 },     // 400г = 1120₽
        { max: 450, price: 107000 },     // 450г = 1070₽
        { max: 500, price: 120000 },     // 500г = 1200₽
        { max: 550, price: 115000 },     // 550г = 1150₽
        { max: 600, price: 110000 },     // 600г = 1100₽
        { max: 650, price: 105000 },     // 650г = 1050₽
        { max: 700, price: 100000 },     // 700г = 1000₽
        { max: 750, price: 115000 },     // 750г = 1150₽
        { max: 800, price: 110000 },     // 800г = 1100₽
        { max: 850, price: 105000 },     // 850г = 1050₽
        { max: 950, price: 100000 },     // 900г-950г = 1000₽
        { max: 1000, price: 95000 },     // 1000г = 950₽
        { max: 1050, price: 90000 },     // 1050г = 900₽
        { max: 1100, price: 85000 },     // 1100г = 850₽
        { max: 1150, price: 80000 },     // 1150г = 800₽
        { max: 1200, price: 93500 },     // 1200г = 935₽
        { max: 1250, price: 88500 },     // 1250г = 885₽
        { max: 1300, price: 83500 },     // 1300г = 835₽
        { max: 1400, price: 78500 },     // 1400г = 785₽
        { max: 1800, price: 80000 }      // 1450г-1800г = 800₽
    ];
    
    /**
     * Calculate delivery price based on weight
     * @param {number} weight - Weight in grams
     * @returns {number} - Price in kopecks
     */
    function calculateDeliveryPrice(weight) {
        for (let rate of DELIVERY_RATES) {
            if (weight <= rate.max) {
                return rate.price;
            }
        }
        return 80000; // Max price for weight > 1800g (800₽)
    }
    
    /**
     * Calculate total order amount
     * @param {object} order - Order object
     * @returns {number} - Total in kopecks
     */
    function calculateOrderTotal(order) {
        let totalKopecks = 0;
        
        // Sum item prices - 7. используем totalMinor если есть
        order.items.forEach(item => {
            const itemPrice = item.totalMinor !== undefined ? item.totalMinor : (item.priceKopecks || 0);
            totalKopecks += itemPrice;
        });
        
        // Add delivery if applicable
        if (order.withDelivery && order.deliveryPriceKopecks) {
            totalKopecks += order.deliveryPriceKopecks;
        }
        
        return totalKopecks;
    }
    
    /**
     * Calculate amount still to be paid
     * @param {object} order - Order object
     * @returns {number} - Amount to pay in kopecks
     */
    function calculateTotalToPay(order) {
        let totalKopecks = 0;
        
        // Unpaid items - 7. используем totalMinor если есть
        order.items.forEach(item => {
            if (!item.isPaid) {
                const itemPrice = item.totalMinor !== undefined ? item.totalMinor : (item.priceKopecks || 0);
                totalKopecks += itemPrice;
            }
        });
        
        // DEBUG - выводим в консоль что происходит
        if (order.id) {
            console.log('=== calculateTotalToPay для заказа #' + order.id + ' ===');
            console.log('Товаров к оплате:', totalKopecks / 100, '₽');
            console.log('order.withDelivery:', order.withDelivery);
            console.log('order.includesDelivery:', order.includesDelivery);
            console.log('order.isDeliveryPaid:', order.isDeliveryPaid);
            console.log('order.deliveryPriceKopecks:', order.deliveryPriceKopecks);
        }
        
        // Delivery adjustment - учитываем разницу между текущей и оплаченной доставкой
        if (order.includesDelivery && order.isDeliveryPaid) {
            // Если доставка оплачена, проверяем нужна ли доплата или есть переплата
            const currentWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams || 0), 0);
            const currentDeliveryPrice = calculateDeliveryPrice(currentWeight);
            // Используем сохраненную оплаченную сумму, если она есть
            const paidDeliveryPrice = order.paidDeliveryPriceKopecks || order.deliveryPriceKopecks || 0;
            const deliveryDifference = currentDeliveryPrice - paidDeliveryPrice;
            
            if (deliveryDifference !== 0) {
                if (order.id) console.log('📊 Разница в доставке:', deliveryDifference / 100, '₽', deliveryDifference > 0 ? '(доплата)' : '(переплата)');
                totalKopecks += deliveryDifference;
            } else {
                if (order.id) console.log('✅ Доставка не изменилась:', paidDeliveryPrice / 100, '₽');
            }
        } else if ((order.withDelivery || order.includesDelivery) && !order.isDeliveryPaid) {
            // Unpaid delivery - добавляем полную стоимость доставки
            if (order.deliveryPriceKopecks) {
                if (order.id) console.log('✅ Добавляем неоплаченную доставку:', order.deliveryPriceKopecks / 100, '₽');
                totalKopecks += order.deliveryPriceKopecks;
            } else if (order.includesDelivery) {
                // Пересчитываем стоимость доставки по текущему весу
                const totalWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams || 0), 0);
                const deliveryPrice = calculateDeliveryPrice(totalWeight);
                if (order.id) console.log('✅ Пересчитываем доставку:', deliveryPrice / 100, '₽');
                totalKopecks += deliveryPrice;
            }
        } else {
            if (order.id) console.log('❌ Доставка не учитывается');
        }
        
        if (order.id) {
            console.log('ИТОГО К ОПЛАТЕ:', totalKopecks / 100, '₽');
            console.log('===================================');
        }
        
        return totalKopecks;
    }
    
    // ========== Currency Conversion ==========
    
    /**
     * Convert RUB to GBP using exchange rate
     * Exchange rate stored as: rate * 100 (e.g., 121.34 stored as 12134)
     * @param {number} kopecks - Amount in kopecks
     * @returns {number|null} - Amount in pence, or null if no rate set
     */
    function convertToGBP(kopecks) {
        const currency = State.getCurrency();
        if (!currency.exchangeRateMinor || currency.exchangeRateMinor <= 0) {
            return null;
        }
        
        // kopecks / (rate/100) = pence
        // Example: 100000 kopecks / (12134/100) = 824 pence = £8.24
        const pence = Math.round((kopecks * 100) / currency.exchangeRateMinor);
        return pence;
    }
    
    /**
     * Convert GBP to RUB using exchange rate
     * @param {number} pence - Amount in pence
     * @returns {number|null} - Amount in kopecks, or null if no rate set
     */
    function convertToRUB(pence) {
        const currency = State.getCurrency();
        if (!currency.exchangeRateMinor || currency.exchangeRateMinor <= 0) {
            return null;
        }
        
        // pence * (rate/100) = kopecks
        const kopecks = Math.round((pence * currency.exchangeRateMinor) / 100);
        return kopecks;
    }
    
    /**
     * Format price with GBP conversion if rate available
     * @param {number} kopecks - Amount in kopecks
     * @returns {string} - Formatted price with optional GBP
     */
    function formatPriceWithGBP(kopecks, hideGBP = false) {
        if (!kopecks || kopecks === 0) return '0.00 ₽';
        
        let result = formatMinor(kopecks, '₽');
        const pence = convertToGBP(kopecks);
        
        // Показываем GBP только если не в клиентском режиме
        if (!hideGBP && pence !== null) {
            result += ` <span style="color: #27ae60; font-weight: 600;">(£${fromMinor(pence)})</span>`;
        }
        
        return result;
    }
    
    return {
        // Utilities
        toMinor,
        fromMinor,
        formatMinor,
        
        // Calculations
        calculateDeliveryPrice,
        calculateOrderTotal,
        calculateTotalToPay,
        
        // Currency
        convertToGBP,
        convertToRUB,
        formatPriceWithGBP
    };
})();

// ============================================================================
// ORDERS MODULE - CRUD operations
// ============================================================================
const Orders = (function() {
    function createOrder(customerData, items, withDelivery) {
        const state = State.get();
        const currency = State.getCurrency();
        const totalWeight = items.reduce((sum, item) => sum + parseInt(item.grams), 0);
        const deliveryPriceKopecks = withDelivery ? Pricing.calculateDeliveryPrice(totalWeight) : 0;
        
        const order = {
            id: state.orderCounter,
            createdAt: Date.now(),  // ✅ NEW: timestamp instead of string
            customer: customerData,
            items: items.map(item => ({
                ...item,
                id: Date.now() + Math.random(),
                productName: item.productName || item.yarn, // 7. Ensure productName
                statuses: [],
                isPaid: false,
                priceKopecks: item.priceKopecks || 0,
                unitPriceMinor: item.unitPriceMinor || 0,  // 7. NEW
                totalMinor: item.totalMinor || 0           // 7. NEW
            })),
            totalWeight: totalWeight,
            
            // ✅ NEW: Structured delivery object (v2.1)
            delivery: {
                enabled: withDelivery,
                priceMinor: deliveryPriceKopecks,
                isPaid: false
            },
            
            // Keep old fields for backward compatibility
            withDelivery: withDelivery,
            includesDelivery: withDelivery,  // ✅ Клиент выбрал доставку при оформлении
            deliveryPriceKopecks: deliveryPriceKopecks,
            isDeliveryPaid: false,
            
            // ✅ NEW: Meta object with currency snapshot (v2.1)
            meta: {
                currency: 'RUB',
                rateGBP_RUB_100: currency.exchangeRateMinor || null  // Save rate at order time
            },
            
            date: new Date().toLocaleString('ru-RU'),  // Keep for display
            privateComments: [],
            publicComments: []
        };
        
        State.addOrder(order);
        
        // Log the new order
        Log.logOrderCreate(order.id, customerData.name, items.length, totalWeight, withDelivery);
        
        return order;
    }
    
    function updateOrder(orderId, updates) {
        const orders = State.getOrders();
        const index = orders.findIndex(o => o.id === orderId);
        
        if (index === -1) return null;
        
        // Полная замена объекта заказа (не merge)
        orders[index] = updates;
        State.updateOrders(orders);
        
        return orders[index];
    }
    
    function deleteOrder(orderId) {
        const orders = State.getOrders();
        const filtered = orders.filter(o => o.id !== orderId);
        State.updateOrders(filtered);
    }
    
    function getOrder(orderId) {
        const orders = State.getOrders();
        return orders.find(o => o.id === orderId);
    }
    
    function getOrderNumber(orderId) {
        const orders = State.getOrders();
        const totalOrders = orders.length;
        const index = orders.findIndex(o => o.id === orderId);
        
        if (index === -1) return orderId; // Fallback to ID if not found
        
        // Самый старый (последний в массиве) = №1, самый новый (первый в массиве) = №N
        return totalOrders - index;
    }
    
    return {
        createOrder,
        updateOrder,
        deleteOrder,
        getOrder,
        getOrderNumber
    };
})();

// ============================================================================
// PAYMENTS MODULE - Payment tracking
// ============================================================================
const Payments = (function() {
    function toggleItemPayment(orderId, itemIndex, isPaid) {
        const order = Orders.getOrder(orderId);
        if (!order || !order.items[itemIndex]) return;
        
        order.items[itemIndex].isPaid = isPaid;
        Orders.updateOrder(orderId, order);
        
        Log.logPaymentChange(orderId, order.customer.name, order.items[itemIndex].yarn, isPaid);
    }
    
    function toggleDeliveryPayment(orderId, isPaid) {
        const order = Orders.getOrder(orderId);
        if (!order) return;
        
        // Если отмечаем как оплаченное - сохраняем текущую сумму доставки
        if (isPaid && !order.isDeliveryPaid) {
            order.paidDeliveryPriceKopecks = order.deliveryPriceKopecks || 0;
            console.log(`💰 Доставка оплачена: ${Pricing.fromMinor(order.paidDeliveryPriceKopecks)}₽`);
        }
        
        // Если снимаем отметку об оплате - удаляем сохраненную сумму
        if (!isPaid && order.isDeliveryPaid) {
            delete order.paidDeliveryPriceKopecks;
            console.log(`🔄 Оплата доставки отменена`);
        }
        
        order.isDeliveryPaid = isPaid;
        Orders.updateOrder(orderId, order);
        
        Log.logPaymentChange(orderId, order.customer.name, 'delivery', isPaid);
    }
    
    function updateItemPrice(orderId, itemIndex, priceRubles) {
        const order = Orders.getOrder(orderId);
        if (!order || !order.items[itemIndex]) return;
        
        const item = order.items[itemIndex];
        const oldPriceKopecks = item.priceKopecks || 0;
        const newPriceKopecks = Pricing.toMinor(priceRubles);
        
        // ✅ Обновляем все три поля для правильного подсчёта
        item.priceKopecks = newPriceKopecks;
        item.totalMinor = newPriceKopecks;  // Общая цена за позицию
        
        // Вычисляем цену за грамм
        if (item.grams && item.grams > 0) {
            item.unitPriceMinor = Math.round(newPriceKopecks / item.grams);
        }
        
        Orders.updateOrder(orderId, order);
        
        if (oldPriceKopecks !== newPriceKopecks) {
            Log.logPriceUpdate(orderId, order.customer.name, item.yarn, oldPriceKopecks, newPriceKopecks);
        }
    }
    
    return {
        toggleItemPayment,
        toggleDeliveryPayment,
        updateItemPrice
    };
})();

// ============================================================================
// LOG MODULE - Audit trail with standardized event types
// ============================================================================
const Log = (function() {
    /**
     * Event types taxonomy:
     * - order.create     - New order created
     * - item.add         - Item added to order
     * - item.merge       - Items merged (same yarn + color)
     * - item.delete      - Item removed from order
     * - item.edit        - Item details edited (color, etc)
     * - item.updateStatus - Item status changed
     * - payment.itemPaid   - Item marked as paid
     * - payment.itemUnpaid - Item marked as unpaid
     * - payment.deliveryPaid - Delivery marked as paid
     * - payment.deliveryUnpaid - Delivery marked as unpaid
     * - price.update     - Item price updated
     * - delivery.request - Delivery added to order
     * - rate.update      - Exchange rate updated
     * - rate.confirm     - Exchange rate confirmed as current
     * - comment.add      - Comment added
     */
    
    /**
     * Add standardized log event
     * @param {string} actor - 'client' or 'seller' or 'system'
     * @param {string} type - Event type (e.g., 'order.create')
     * @param {object} payload - Event data
     */
    function addEvent(actor, type, payload) {
        const event = {
            ts: Date.now(),
            time: new Date().toLocaleString('ru-RU'),
            actor: actor,
            type: type,
            payload: payload
        };
        
        State.addChangeLog(event);
        
        return event;
    }
    
    /**
     * Legacy wrapper for backward compatibility
     * @deprecated Use addEvent instead
     */
    function addChange(orderId, type, customerName, details) {
        // Map old types to new standard types
        const typeMapping = {
            'new_order': 'order.create',
            'item_merge': 'item.merge',
            'status_change': 'item.updateStatus',
            'payment_change': 'payment.itemPaid',
            'price_change': 'price.update',
            'comment': 'comment.add'
        };
        
        const standardType = typeMapping[type] || type;
        
        addEvent('seller', standardType, {
            orderId: orderId,
            customerName: customerName,
            details: details
        });
    }
    
    // Convenience methods for common events
    
    function logOrderCreate(orderId, customerName, itemCount, weight, withDelivery) {
        return addEvent('client', 'order.create', {
            orderId: orderId,
            customerName: customerName,
            itemCount: itemCount,
            totalWeight: weight,
            withDelivery: withDelivery
        });
    }
    
    function logItemAdd(orderId, customerName, yarn, color, grams) {
        return addEvent('client', 'item.add', {
            orderId: orderId,
            customerName: customerName,
            yarn: yarn,
            color: color,
            grams: grams
        });
    }
    
    function logItemMerge(orderId, customerName, yarn, color, totalGrams) {
        return addEvent('client', 'item.merge', {
            orderId: orderId,
            customerName: customerName,
            yarn: yarn,
            color: color,
            totalGrams: totalGrams
        });
    }
    
    function logItemStatusUpdate(orderId, customerName, yarn, oldStatus, newStatus) {
        return addEvent('seller', 'item.updateStatus', {
            orderId: orderId,
            customerName: customerName,
            yarn: yarn,
            oldStatus: oldStatus,
            newStatus: newStatus
        });
    }
    
    function logPriceUpdate(orderId, customerName, yarn, oldPrice, newPrice) {
        return addEvent('seller', 'price.update', {
            orderId: orderId,
            customerName: customerName,
            yarn: yarn,
            oldPriceKopecks: oldPrice,
            newPriceKopecks: newPrice
        });
    }
    
    function logPaymentChange(orderId, customerName, itemOrDelivery, isPaid) {
        const type = itemOrDelivery === 'delivery' 
            ? (isPaid ? 'payment.deliveryPaid' : 'payment.deliveryUnpaid')
            : (isPaid ? 'payment.itemPaid' : 'payment.itemUnpaid');
            
        return addEvent('seller', type, {
            orderId: orderId,
            customerName: customerName,
            item: itemOrDelivery,
            isPaid: isPaid
        });
    }
    
    function logRateUpdate(rate, source) {
        return addEvent('seller', 'rate.update', {
            rate: rate,
            source: source
        });
    }
    
    function logRateConfirm() {
        return addEvent('seller', 'rate.confirm', {});
    }
    
    function logCommentAdd(orderId, customerName, isPrivate, comment) {
        return addEvent('seller', 'comment.add', {
            orderId: orderId,
            customerName: customerName,
            isPrivate: isPrivate,
            comment: comment
        });
    }
    
    function getRecentChanges(limit = 50) {
        const state = State.get();
        return state.changeLog.slice(0, limit);
    }
    
    function getEventsByType(type, limit = 50) {
        const state = State.get();
        return state.changeLog
            .filter(log => log.type === type)
            .slice(0, limit);
    }
    
    function getEventsByOrder(orderId) {
        const state = State.get();
        return state.changeLog
            .filter(log => log.payload && log.payload.orderId === orderId);
    }
    
    function clearOldLogs(daysToKeep = 30) {
        const state = State.get();
        const cutoffTimestamp = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
        
        const filtered = state.changeLog.filter(log => {
            // Support both old format (time string) and new format (ts)
            if (log.ts) {
                return log.ts >= cutoffTimestamp;
            } else if (log.time) {
                const logDate = new Date(log.time);
                return logDate.getTime() >= cutoffTimestamp;
            }
            return true;
        });
        
        State.setState({ changeLog: filtered });
    }
    
    return {
        // New standardized API
        addEvent,
        logOrderCreate,
        logItemAdd,
        logItemMerge,
        logItemStatusUpdate,
        logPriceUpdate,
        logPaymentChange,
        logRateUpdate,
        logRateConfirm,
        logCommentAdd,
        getEventsByType,
        getEventsByOrder,
        
        // Legacy API (deprecated but kept for compatibility)
        addChange,
        getRecentChanges,
        clearOldLogs
    };
})();

// Export for use in HTML
window.YarnOrderApp = {
    State,
    Storage,
    Pricing,
    Orders,
    Payments,
    Log
};

// ============================================================================
// TEMPLATES MODULE - HTML generators
// ============================================================================
const Templates = (function() {
    const statusLabels = {
        'paid': 'Оплачен',
        'processing': 'В обработке',
        'wound': 'Намотан',
        'shipped': 'Отправлен',
        'out-of-stock': 'Нет в наличии'
    };
    
    function orderItem(item) {
        const statusDots = item.statuses ? item.statuses.map(status => 
            `<span class="status-dot ${status}" title="${statusLabels[status]}"></span>`
        ).join('') : '';
        
        const isWound = item.statuses && item.statuses.includes('wound');
        const isPaid = item.isPaid === true;
        
        // Кнопка редактирования - заблокирована после намотки
        const editButton = !isWound ? 
            `<button class="client-edit-btn" data-action="editColor" data-item-id="${item.id}" aria-label="Редактировать цвет" title="Редактировать цвет">✏️</button>` : 
            `<button class="client-edit-btn disabled" disabled aria-label="Редактирование заблокировано после намотки" title="Нельзя изменить цвет после намотки">🔒</button>`;
        
        // Кнопка удаления - скрыта для оплаченных товаров
        const deleteButton = !isPaid ? 
            `<button class="delete-btn" data-action="deleteItem" data-item-id="${item.id}" aria-label="Удалить товар" title="Удалить">×</button>` :
            `<span class="paid-indicator" title="Товар оплачен - удаление невозможно" style="color: #27ae60; font-weight: 600; font-size: 14px; padding: 8px;">✓ Оплачено</span>`;
        
        const priceDisplay = Pricing.fromMinor(item.priceKopecks || 0);
        
        return `
            <div class="order-item">
                <div class="order-item-info">
                    <div class="order-item-name">
                        ${item.yarn}
                        <span class="order-item-color">• ${item.color}</span>
                        <span class="order-item-details">• ${item.grams} г</span>
                        <span class="order-item-details" style="color: #8B7355; font-weight: 600;">• ${priceDisplay} ₽</span>
                        ${statusDots ? `<span class="status-indicator">${statusDots}</span>` : ''}
                        ${editButton}
                    </div>
                </div>
                ${deleteButton}
            </div>
        `;
    }
    
    // Helper functions for action badge
    function requiresAction(order) {
        // Требуется действие если: есть неоплаченные позиции ИЛИ есть позиции без статуса
        const hasUnpaidItems = order.items.some(item => !item.isPaid);
        const hasUnpaidDelivery = order.withDelivery && !order.isDeliveryPaid;
        const hasItemsWithoutStatus = order.items.some(item => !item.statuses || item.statuses.length === 0);
        
        return hasUnpaidItems || hasUnpaidDelivery || hasItemsWithoutStatus;
    }
    
    function countRequiredActions(order) {
        let count = 0;
        
        // Считаем неоплаченные позиции
        count += order.items.filter(item => !item.isPaid).length;
        
        // Считаем неоплаченную доставку
        if (order.withDelivery && !order.isDeliveryPaid) {
            count += 1;
        }
        
        // Считаем позиции без статусов
        count += order.items.filter(item => !item.statuses || item.statuses.length === 0).length;
        
        return count;
    }
    
    function sellerOrder(order, orderNumber) {
    const commentsCount = (order.privateComments?.length || 0) + (order.publicComments?.length || 0);
    
    return `
        <div class="seller-order-compact" id="order-${order.id}">
            <div class="seller-order-header-compact">
                <div class="seller-order-info-compact">
                    <label>Заказ №</label>
                    <span>${orderNumber}</span>
                </div>
                <div class="seller-order-info-compact">
                    <label>Клиент</label>
                    <span>${order.customer.name}</span>
                </div>
                <div class="seller-order-info-compact">
                    <label>Телефон</label>
                    <span>${order.customer.phone}</span>
                </div>
                <div class="seller-order-info-compact">
                    <label>Email</label>
                    <span>${order.customer.email}</span>
                </div>
                <div class="seller-order-info-compact">
                    <label>Адрес</label>
                    <span>${order.customer.address}</span>
                </div>
                <div class="seller-order-info-compact">
                    <label>Дата</label>
                    <span>${order.date}</span>
                </div>
                <div style="margin-left: auto; display: flex; gap: 8px; align-items: center;">
                    ${requiresAction(order) ? `<span class="action-badge" title="Требуется действие">${countRequiredActions(order)}</span>` : ''}
                    ${commentsCount > 0 ? `<button class="comments-badge" data-action="toggleCommentsFromHeader" data-order-id="${order.id}" title="Комментарии: ${commentsCount}">💬 ${commentsCount}</button>` : ''}
                    <button class="order-toggle-btn" data-action="toggleOrder" data-order-id="${order.id}">
                        <span class="order-toggle-icon">▼</span>
                    </button>
                </div>
            </div>
            <div class="order-collapsible-content">
                ${paymentSection(order)}
                ${itemsSection(order)}
                ${trackingSection(order)}
                ${commentsSection(order)}
            </div>
        </div>
    `;
}
    
    function paymentSection(order) {
        const items = order.items.map((item, index) => {
            // Используем totalMinor если есть, иначе priceKopecks
            const itemTotal = item.totalMinor !== undefined ? item.totalMinor : (item.priceKopecks || 0);
            const priceRubles = Pricing.fromMinor(itemTotal);
            
            return `
            <div class="payment-item ${item.isPaid ? 'paid' : ''}">
                <div class="payment-item-info">
                    ${item.yarn} - ${item.color} (${item.grams}г)
                </div>
                <input type="number" class="price-input" value="${priceRubles}" step="0.01"
                       data-action="updatePrice" data-order-id="${order.id}" data-item-index="${index}">
                <div class="payment-item-price">${Pricing.formatPriceWithGBP(itemTotal)}</div>
                <input type="checkbox" class="payment-checkbox" ${item.isPaid ? 'checked' : ''}
                       data-action="togglePayment" data-order-id="${order.id}" data-item-index="${index}" data-type="item">
            </div>
        `;
        }).join('');
        
        // Проверяем текущую стоимость доставки vs оплаченную
        const currentWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams), 0);
        const currentDeliveryPrice = Pricing.calculateDeliveryPrice(currentWeight);
        // Используем сохраненную оплаченную сумму, если она есть
        const paidDeliveryPrice = order.paidDeliveryPriceKopecks || order.deliveryPriceKopecks || 0;
        const deliverySurcharge = currentDeliveryPrice - paidDeliveryPrice;
        
        // Показываем доставку если: 1) продавец отметил withDelivery, 2) клиент изначально выбрал доставку, 3) есть недоплата
        const shouldShowDelivery = order.withDelivery || order.includesDelivery || (deliverySurcharge > 0);
        
        // Определяем цену доставки для отображения
        const displayDeliveryPrice = order.deliveryPriceKopecks || currentDeliveryPrice;
        
        const delivery = shouldShowDelivery ? `
            <div class="payment-item ${order.isDeliveryPaid ? 'paid' : ''}">
                <div class="payment-item-info">
                    <strong>Доставка</strong>
                    <span style="color: #666; font-size: 12px;">(${currentWeight}г)</span>
                </div>
                <div class="payment-item-price">${Pricing.formatPriceWithGBP(displayDeliveryPrice)}</div>
                <input type="checkbox" class="payment-checkbox" ${order.isDeliveryPaid ? 'checked' : ''}
                       data-action="togglePayment" data-order-id="${order.id}" data-type="delivery">
            </div>
        ` : '';
        
        // Уведомление о недоплате или переплате доставки
        let deliveryNotice = '';
        if (deliverySurcharge > 0 && order.includesDelivery) {
            // Доставка стала дороже, нужна доплата
            deliveryNotice = `
                <div style="background: #fff3cd; padding: 12px; border-radius: 8px; margin: 12px 0; border-left: 4px solid #f39c12;">
                    <strong>⚠️ Требуется доплата доставки: ${Pricing.formatPriceWithGBP(deliverySurcharge)}</strong>
                    <div style="font-size: 12px; color: #666; margin-top: 4px;">Текущая стоимость: ${Pricing.formatPriceWithGBP(currentDeliveryPrice)}, оплачено: ${Pricing.formatPriceWithGBP(paidDeliveryPrice)}</div>
                </div>
            `;
        } else if (deliverySurcharge < 0 && order.includesDelivery) {
            // Доставка стала дешевле, нужно вернуть переплату
            const overpaymentAmount = Math.abs(deliverySurcharge);
            deliveryNotice = `
                <div style="background: #d4edda; padding: 12px; border-radius: 8px; margin: 12px 0; border-left: 4px solid #27ae60;">
                    <strong>✅ Доставка стала дешевле: вернуть клиенту ${Pricing.formatPriceWithGBP(overpaymentAmount)}</strong>
                    <div style="font-size: 12px; color: #666; margin-top: 4px;">Текущая стоимость: ${Pricing.formatPriceWithGBP(currentDeliveryPrice)}, оплачено: ${Pricing.formatPriceWithGBP(paidDeliveryPrice)}</div>
                    <button class="status-btn" data-action="applyOverpayment" data-order-id="${order.id}" style="margin-top: 8px; font-size: 13px;">
                        💰 Зачесть переплату в товары
                    </button>
                </div>
            `;
        }
        
        // Считаем общую сумму заказа
        const orderTotal = Pricing.calculateOrderTotal(order);
        
        return `
            <div class="payment-status-section">
                <div class="payment-status-title">💰 Статус оплаты</div>
                ${items}
                ${delivery}
                ${deliveryNotice}
                <div class="payment-summary">
                    <span>Итого к оплате:</span>
                    <span class="payment-to-pay">${Pricing.formatPriceWithGBP(Pricing.calculateTotalToPay(order))}</span>
                </div>
                <div class="payment-summary" style="margin-top: 8px; padding-top: 8px; border-top: 2px solid var(--border-color); font-size: 16px;">
                    <span><strong>Итого по заказу:</strong></span>
                    <span style="font-weight: 700; color: var(--text-primary);">${Pricing.formatPriceWithGBP(orderTotal)}</span>
                </div>
            </div>
        `;
    }
    
    function itemsSection(order) {
        const itemsHtml = order.items.map((item, index) => `
            <div class="seller-order-item">
                <div class="seller-item-top">
                    <div class="seller-item-info">
                        <div class="seller-item-name">${item.yarn}</div>
                        <div class="seller-item-details">Цвет: ${item.color} • Вес: ${item.grams} г</div>
                    </div>
                    <div style="display: flex; gap: 8px;">
                        <button class="edit-item-btn" data-action="editItem" data-order-id="${order.id}" data-item-index="${index}">
                            ✏️ Редактировать
                        </button>
                        <button class="delete-item-btn" data-action="deleteOrderItem" data-order-id="${order.id}" data-item-index="${index}" 
                                style="background: #e74c3c; color: white; border: none; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 14px;"
                                title="Удалить товар">
                            🗑️
                        </button>
                    </div>
                </div>
                <div class="status-buttons">
                    <button class="status-btn paid ${(item.statuses || []).includes('paid') ? 'active' : ''}" 
                            data-action="setStatus" data-order-id="${order.id}" data-item-index="${index}" data-status="paid"
                            title="Нажмите чтобы ${(item.statuses || []).includes('paid') ? 'снять' : 'установить'} статус">
                        Оплачен
                    </button>
                    <button class="status-btn processing ${(item.statuses || []).includes('processing') ? 'active' : ''}"
                            data-action="setStatus" data-order-id="${order.id}" data-item-index="${index}" data-status="processing"
                            title="Нажмите чтобы ${(item.statuses || []).includes('processing') ? 'снять' : 'установить'} статус">
                        В обработке
                    </button>
                    <button class="status-btn wound ${(item.statuses || []).includes('wound') ? 'active' : ''}"
                            data-action="setStatus" data-order-id="${order.id}" data-item-index="${index}" data-status="wound"
                            title="Нажмите чтобы ${(item.statuses || []).includes('wound') ? 'снять' : 'установить'} статус">
                        Намотан
                    </button>
                    <button class="status-btn shipped ${(item.statuses || []).includes('shipped') ? 'active' : ''}"
                            data-action="setStatus" data-order-id="${order.id}" data-item-index="${index}" data-status="shipped"
                            title="Нажмите чтобы ${(item.statuses || []).includes('shipped') ? 'снять' : 'установить'} статус">
                        Отправлен
                    </button>
                    <button class="status-btn out-of-stock ${(item.statuses || []).includes('out-of-stock') ? 'active' : ''}"
                            data-action="setStatus" data-order-id="${order.id}" data-item-index="${index}" data-status="out-of-stock"
                            title="Нажмите чтобы ${(item.statuses || []).includes('out-of-stock') ? 'снять' : 'установить'} статус">
                        Нет в наличии
                    </button>
                </div>
            </div>
        `).join('');
        
        return `
            ${itemsHtml}
            <button class="add-item-btn" data-action="addOrderItem" data-order-id="${order.id}" 
                    style="margin-top: 12px; width: 100%; background: #27ae60; color: white; border: none; padding: 12px; border-radius: 10px; cursor: pointer; font-size: 15px; font-weight: 600;">
                ➕ Добавить товар в заказ
            </button>
        `;
    }
    
    function trackingSection(order) {
        const hasTracking = order.trackingNumber && order.trackingNumber.trim();
        return `
            <div style="margin-top: 16px; padding: 16px; background: #f0f7ff; border-radius: 10px; border-left: 4px solid #3498db;">
                <div style="display: flex; align-items: center; gap: 12px; margin-bottom: ${hasTracking ? '12px' : '0'};">
                    <span style="font-weight: 600; color: #2c3e50;">📦 Номер отслеживания:</span>
                    ${hasTracking ? 
                        `<span style="font-family: monospace; color: #3498db; font-size: 15px;">${order.trackingNumber}</span>` : 
                        `<span style="color: #95a5a6; font-style: italic;">Не указан</span>`
                    }
                    <button class="edit-item-btn" data-action="editTracking" data-order-id="${order.id}" 
                            style="margin-left: auto;">
                        ✏️ ${hasTracking ? 'Изменить' : 'Добавить'}
                    </button>
                </div>
            </div>
        `;
    }
    
    function commentsSection(order) {
        return `
            <div class="comments-section">
                <button class="comments-toggle" data-action="toggleComments" data-order-id="${order.id}">
                    <span>💬 Комментарии (${(order.privateComments?.length || 0) + (order.publicComments?.length || 0)})</span>
                    <span class="comments-toggle-icon">▼</span>
                </button>
                <div class="comments-content" id="comments-${order.id}">
                    <div class="comments-tabs">
                        <button class="comment-tab active" data-action="switchTab" data-order-id="${order.id}" data-tab="private">
                            🔒 Приватные (${order.privateComments?.length || 0})
                        </button>
                        <button class="comment-tab" data-action="switchTab" data-order-id="${order.id}" data-tab="public">
                            👥 Общие (${order.publicComments?.length || 0})
                        </button>
                    </div>
                    <div class="comment-panel active" id="private-${order.id}">
                        <div class="comment-list">${renderComments(order.privateComments || [])}</div>
                        <div class="comment-input-area">
                            <textarea class="comment-input" id="private-input-${order.id}" placeholder="Приватная заметка..."></textarea>
                            <button class="add-comment-btn" data-action="addComment" data-order-id="${order.id}" data-type="private">Добавить</button>
                        </div>
                    </div>
                    <div class="comment-panel" id="public-${order.id}">
                        <div class="comment-list">${renderComments(order.publicComments || [])}</div>
                        <div class="comment-input-area">
                            <textarea class="comment-input" id="public-input-${order.id}" placeholder="Сообщение для клиента..."></textarea>
                            <button class="add-comment-btn" data-action="addComment" data-order-id="${order.id}" data-type="public">Добавить</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    function clientCommentsSection(order) {
        return `
            <div class="comments-section">
                <button class="comments-toggle" data-action="toggleComments" data-order-id="${order.id}">
                    <span>💬 Сообщения (${order.publicComments?.length || 0})</span>
                    <span class="comments-toggle-icon">▼</span>
                </button>
                <div class="comments-content" id="comments-${order.id}">
                    <div class="comment-panel active" id="public-${order.id}">
                        <div class="comment-list">${renderComments(order.publicComments || [])}</div>
                        <div class="comment-input-area">
                            <textarea class="comment-input" id="public-input-${order.id}" placeholder="Ваше сообщение..."></textarea>
                            <button class="add-comment-btn" data-action="addComment" data-order-id="${order.id}" data-type="public">Отправить</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    function renderComments(comments) {
        if (!comments || comments.length === 0) {
            return '<div class="empty-comments">Комментариев пока нет</div>';
        }
        return comments.map(comment => `
            <div class="comment-item ${comment.author === 'seller' ? 'seller-comment' : 'client-comment'}">
                <div class="comment-header">
                    <span class="comment-author ${comment.author}">${comment.author === 'seller' ? 'Продавец' : 'Клиент'}</span>
                    <span class="comment-time">${comment.time}</span>
                </div>
                <div class="comment-text">${comment.text}</div>
            </div>
        `).join('');
    }
    
    
    function changeLogEntry(log) {
        // Support both new format (with payload) and legacy format
        const isNewFormat = log.payload !== undefined;
        
        let icon = '📝';
        let bgColor = '#f8f8f8';
        let content = '';
        let header = '';
        
        if (isNewFormat) {
            // New standardized format
            const actor = log.actor === 'client' ? '👤 Клиент' : '👨‍💼 Продавец';
            
            // Map event types to icons and colors
            const eventConfig = {
                'order.create': { icon: '🆕', color: '#d4edda' },
                'item.add': { icon: '➕', color: '#d4edda' },
                'item.merge': { icon: '🔗', color: '#fff3cd' },
                'item.delete': { icon: '➖', color: '#f8d7da' },
                'item.edit': { icon: '✏️', color: '#fff3cd' },
                'item.updateStatus': { icon: '🔄', color: '#d1ecf1' },
                'payment.itemPaid': { icon: '💰', color: '#d4edda' },
                'payment.itemUnpaid': { icon: '⏳', color: '#fff3cd' },
                'payment.deliveryPaid': { icon: '🚚', color: '#d4edda' },
                'payment.deliveryUnpaid': { icon: '📦', color: '#fff3cd' },
                'price.update': { icon: '💵', color: '#d1ecf1' },
                'rate.update': { icon: '💱', color: '#cfe2ff' },
                'rate.confirm': { icon: '✅', color: '#d1f2eb' },
                'comment.add': { icon: '💬', color: '#e7e7e7' }
            };
            
            const config = eventConfig[log.type] || { icon: '📝', color: '#f8f8f8' };
            icon = config.icon;
            bgColor = config.color;
            
            // Build readable content based on event type
            if (log.type === 'order.create') {
                header = `Новый заказ #${Orders.getOrderNumber(log.payload.orderId)} • ${log.payload.customerName}`;
                content = `${log.payload.itemCount} товар(ов), ${log.payload.totalWeight}г. ${log.payload.withDelivery ? 'С доставкой' : 'Без доставки'}`;
            } else if (log.type === 'item.merge') {
                header = `Заказ #${Orders.getOrderNumber(log.payload.orderId)} • ${log.payload.customerName}`;
                content = `Объединены: ${log.payload.yarn} - ${log.payload.color}. Итого: ${log.payload.totalGrams}г`;
            } else if (log.type === 'item.updateStatus') {
                header = `Заказ #${Orders.getOrderNumber(log.payload.orderId)} • ${log.payload.customerName}`;
                content = `Статус изменён: ${log.payload.yarn}`;
            } else if (log.type === 'price.update') {
                header = `Заказ #${Orders.getOrderNumber(log.payload.orderId)} • ${log.payload.customerName}`;
                const oldPrice = Pricing.fromMinor(log.payload.oldPriceKopecks);
                const newPrice = Pricing.fromMinor(log.payload.newPriceKopecks);
                content = `Цена ${log.payload.yarn}: ${oldPrice}₽ → ${newPrice}₽`;
            } else if (log.type.startsWith('payment.')) {
                header = `Заказ #${Orders.getOrderNumber(log.payload.orderId)} • ${log.payload.customerName}`;
                const status = log.payload.isPaid ? 'оплачен' : 'не оплачен';
                const item = log.payload.item === 'delivery' ? 'Доставка' : log.payload.item;
                content = `${item} ${status}`;
            } else if (log.type === 'rate.update') {
                header = 'Курс валюты обновлён';
                content = `1 £ = ${log.payload.rate} ₽ (источник: ${log.payload.source})`;
            } else if (log.type === 'rate.confirm') {
                header = 'Курс подтверждён';
                content = 'Текущий курс актуален';
            } else {
                header = `Заказ #${log.payload.orderId ? Orders.getOrderNumber(log.payload.orderId) : '?'} • ${log.payload.customerName || '?'}`;
                content = log.payload.details || JSON.stringify(log.payload);
            }
            
            return `
                <div class="change-log-item" style="background: ${bgColor}">
                    <div class="change-log-icon">${icon}</div>
                    <div class="change-log-content">
                        <div class="change-log-header">
                            ${header} <span style="color: #888; font-size: 12px;">${actor}</span>
                        </div>
                        <div class="change-log-details">${content}</div>
                        <div class="change-log-time">${log.time}</div>
                    </div>
                </div>
            `;
        } else {
            // Legacy format support
            if (log.type === 'color_change') {
                icon = '🎨';
                bgColor = '#fff3cd';
            } else if (log.type === 'status_change') {
                icon = '🔄';
                bgColor = '#d1ecf1';
            } else if (log.type === 'new_order') {
                icon = '🆕';
                bgColor = '#d4edda';
            }
            
            return `
                <div class="change-log-item" style="background: ${bgColor}">
                    <div class="change-log-icon">${icon}</div>
                    <div class="change-log-content">
                        <div class="change-log-header">
                            <strong>Заказ #${Orders.getOrderNumber(log.orderId)}</strong> • ${log.customerName}
                        </div>
                        <div class="change-log-details">${log.details}</div>
                        <div class="change-log-time">${log.time}</div>
                    </div>
                </div>
            `;
        }
    }
    
    return {
        orderItem,
        sellerOrder,
        changeLogEntry,
        clientCommentsSection
    };
})();

// ============================================================================
// UI MODULE - Rendering and event handling
// ============================================================================
const UI = (function() {
    let currentFilter = 'all';
    let searchQuery = '';
    
    // ========== Rendering Functions ==========
    
    function renderClientOrder() {
        const session = State.getClientSession();
        const container = document.getElementById('orderList');
        
        console.log('renderClientOrder вызван. Session:', session);
        console.log('orderItems:', session.orderItems);
        console.log('currentOrderId:', session.currentOrderId);
        
        // Если есть товары в черновике (создается новый заказ), показываем черновик
        if (!session.currentOrderId && session.orderItems && session.orderItems.length > 0) {
            console.log('Показываем черновик с товарами');
            const totalWeight = session.orderItems.reduce((sum, item) => sum + parseInt(item.grams), 0);
            const deliveryPriceKopecks = Pricing.calculateDeliveryPrice(totalWeight);
            const itemsTotalKopecks = session.orderItems.reduce((sum, item) => {
                const itemPrice = item.totalMinor !== undefined ? item.totalMinor : (item.priceKopecks || 0);
                return sum + itemPrice;
            }, 0);
            
            let weightClass = '';
            if (totalWeight >= 1500) weightClass = 'danger';
            else if (totalWeight >= 1200) weightClass = 'warning';
            
            const itemsTotalDisplay = Pricing.fromMinor(itemsTotalKopecks);
            const deliveryDisplay = Pricing.fromMinor(deliveryPriceKopecks);
            
            container.innerHTML = `
                <div class="order-weight-info ${weightClass}">
                    <div style="width: 100%;">
                        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                            <div>
                                <span class="weight-label">Общий вес:</span>
                                <span class="weight-value ${weightClass}">${totalWeight} г / 1800 г</span>
                            </div>
                            <div style="text-align: right;">
                                <div><span class="weight-label">Товары:</span> ${itemsTotalDisplay} ₽</div>
                                <div><span class="weight-label">Доставка:</span> ${deliveryDisplay} ₽</div>
                            </div>
                        </div>
                        <div class="weight-bar">
                            <div class="weight-bar-fill ${weightClass}" style="width: ${Math.min(totalWeight / 18, 100)}%"></div>
                        </div>
                    </div>
                </div>
                ${session.orderItems.map(item => Templates.orderItem(item)).join('')}
                <div class="payment-buttons">
                    <button class="add-to-order-btn" data-action="scrollToAdd" aria-label="Прокрутить к форме добавления товара" title="Добавить ещё товары">Добавить к этому заказу</button>
                    <button class="payment-btn delivery" data-action="submitOrder" data-delivery="true" aria-label="Оформить заказ с доставкой" title="Доставка почтой">Оплатить</button>
                </div>
            `;
            
            updateContactFormVisibility();
            return;
        }
        
        // Если у клиента есть email, показываем все его заказы
        if (session.email) {
            const allOrders = State.getOrders();
            const clientOrders = allOrders.filter(order => 
                order.customer.email.toLowerCase() === session.email.toLowerCase()
            );
            
            // Если есть заказы клиента, показываем их список
            if (clientOrders.length > 0) {
                const ordersListHtml = clientOrders.map(order => {
                    const isCurrentOrder = order.id === session.currentOrderId;
                    const totalPrice = Pricing.formatPriceWithGBP(Pricing.calculateOrderTotal(order));
                    const totalToPay = Pricing.calculateTotalToPay(order);
                    const isShipped = order.items.some(item => 
                        item.statuses && item.statuses.includes('shipped')
                    );
                    
                    // Проверяем доставку
                    const totalWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams), 0);
                    const currentDeliveryPrice = Pricing.calculateDeliveryPrice(totalWeight);
                    const paidDeliveryPrice = order.paidDeliveryPriceKopecks || order.deliveryPriceKopecks || 0;
                    const deliveryDiff = currentDeliveryPrice - paidDeliveryPrice;
                    
                    // Уведомление об изменении стоимости доставки
                    let deliverySurchargeNotice = '';
                    if (deliveryDiff !== 0 && order.includesDelivery && order.isDeliveryPaid && isCurrentOrder) {
                        if (deliveryDiff > 0) {
                            deliverySurchargeNotice = `
                                <div style="background: var(--accent-yellow); padding: 12px; border-radius: 8px; margin-top: 12px; border-left: 3px solid #f39c12;">
                                    <strong>⚠️ Требуется доплата:</strong> ${Pricing.formatPriceWithGBP(deliveryDiff)}
                                </div>
                            `;
                        } else {
                            deliverySurchargeNotice = `
                                <div style="background: var(--accent-green); padding: 12px; border-radius: 8px; margin-top: 12px; border-left: 3px solid var(--status-paid);">
                                    <strong>✅ Будет вычтено:</strong> ${Pricing.formatPriceWithGBP(Math.abs(deliveryDiff))}
                                </div>
                            `;
                        }
                    }
                    
                    // Показываем информацию о доставке
                    let deliveryInfo = '';
                    if (order.includesDelivery) {
                        const displayDeliveryPrice = order.deliveryPriceKopecks || currentDeliveryPrice;
                        if (order.isDeliveryPaid) {
                            let deliveryText = `Оплачено: <strong>${Pricing.formatPriceWithGBP(paidDeliveryPrice)}</strong>`;
                            if (deliveryDiff !== 0) {
                                deliveryText += ` → Текущая: <strong>${Pricing.formatPriceWithGBP(currentDeliveryPrice)}</strong>`;
                            }
                            deliveryInfo = `
                                <div style="background: var(--accent-green); padding: 12px; border-radius: 8px; margin-top: 12px;">
                                    📦 Доставка: ${deliveryText} <span style="color: var(--status-paid);">✅</span>
                                </div>
                            `;
                        } else {
                            deliveryInfo = `
                                <div style="background: var(--accent-yellow); padding: 12px; border-radius: 8px; margin-top: 12px;">
                                    📦 Доставка: <strong>${Pricing.formatPriceWithGBP(displayDeliveryPrice)}</strong> <span style="color: #e74c3c;">⚠️ Не оплачена</span>
                                </div>
                            `;
                        }
                    }
                    
                    // Товары (только если это текущий заказ)
                    let itemsHtml = '';
                    if (isCurrentOrder) {
                        // Номер отслеживания (если есть)
                        let trackingHtml = '';
                        if (order.trackingNumber && order.trackingNumber.trim()) {
                            trackingHtml = `
                                <div style="margin-top: 16px; padding: 16px; background: #e3f2fd; border-radius: 10px; border-left: 4px solid #2196f3;">
                                    <div style="font-weight: 600; color: #1976d2; margin-bottom: 8px;">📦 Номер отслеживания:</div>
                                    <div style="font-family: monospace; font-size: 16px; color: #1565c0; font-weight: 500;">${order.trackingNumber}</div>
                                </div>
                            `;
                        }
                        
                        itemsHtml = `
                            <div style="margin-top: 16px;">
                                ${order.items.map(item => Templates.orderItem(item)).join('')}
                            </div>
                            ${trackingHtml}
                            ${Templates.clientCommentsSection(order)}
                        `;
                    }
                    
                    // Кнопки действий
                    let actionButtons = '';
                    if (isCurrentOrder) {
                        actionButtons = `
                            <div style="display: flex; gap: 8px; margin-top: 12px;">
                                ${!isShipped ? '<button class="add-to-order-btn" data-action="scrollToAdd" style="flex: 1;">Добавить товары</button>' : ''}
                                <button class="add-to-order-btn" data-action="createNewOrder" style="flex: 1;">Создать новый заказ</button>
                            </div>
                        `;
                    } else {
                        actionButtons = `
                            <button class="add-to-order-btn" data-action="switchToOrder" data-order-id="${order.id}" style="margin-top: 12px; width: 100%;">
                                Открыть этот заказ
                            </button>
                        `;
                    }
                    
                    return `
                        <div style="background: ${isCurrentOrder ? 'var(--accent-green)' : 'var(--bg-secondary)'}; padding: 20px; border-radius: 12px; margin-bottom: 16px; border-left: 4px solid ${isCurrentOrder ? 'var(--status-paid)' : 'var(--border-color)'};">
                            <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 12px;">
                                <div>
                                    <h3 style="margin: 0 0 4px 0; color: var(--text-primary);">
                                        ${isCurrentOrder ? '🔵' : '📦'} Заказ #${Orders.getOrderNumber(order.id)}
                                        ${isCurrentOrder ? '<span style="font-size: 14px; font-weight: normal; color: var(--text-secondary);"> (текущий)</span>' : ''}
                                    </h3>
                                    <p style="margin: 0; color: var(--text-secondary); font-size: 14px;">
                                        Дата: ${order.date}
                                        ${isShipped ? '<br><strong style="color: var(--status-wound);">📬 Отправлен</strong>' : ''}
                                    </p>
                                </div>
                                <div style="text-align: right;">
                                    <div style="font-size: 18px; font-weight: 600; color: var(--text-primary);">${totalPrice}</div>
                                    ${totalToPay > 0 ? `<div style="font-size: 14px; color: #e74c3c;">К оплате: ${Pricing.formatPriceWithGBP(totalToPay)}</div>` : ''}
                                </div>
                            </div>
                            
                            <div style="color: var(--text-secondary); font-size: 14px;">
                                Товаров: ${order.items.length} шт. (${order.totalWeight}г)
                            </div>
                            
                            ${deliveryInfo}
                            ${deliverySurchargeNotice}
                            ${itemsHtml}
                            ${actionButtons}
                        </div>
                    `;
                }).join('');
                
                container.innerHTML = `
                    <div style="margin-bottom: 20px;">
                        <h3 style="color: var(--text-primary); margin-bottom: 16px;">📋 Ваши заказы</h3>
                        ${ordersListHtml}
                    </div>
                `;
                updateContactFormVisibility();
                return;
            }
        }
        
        // Если есть текущий заказ (но нет email в сессии - старый формат)
        if (session.currentOrderId) {
            const order = Orders.getOrder(session.currentOrderId);
            if (order) {
                const totalPrice = Pricing.formatPriceWithGBP(Pricing.calculateOrderTotal(order));
                const isShipped = order.items.some(item => 
                    item.statuses && item.statuses.includes('shipped')
                );
                const itemsHtml = order.items.map(item => Templates.orderItem(item)).join('');
                
                const totalWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams), 0);
                const currentDeliveryPrice = Pricing.calculateDeliveryPrice(totalWeight);
                const paidDeliveryPrice = order.paidDeliveryPriceKopecks || order.deliveryPriceKopecks || 0;
                const deliveryDiff = currentDeliveryPrice - paidDeliveryPrice;
                
                let deliverySurchargeNotice = '';
                if (deliveryDiff !== 0 && order.includesDelivery && order.isDeliveryPaid) {
                    if (deliveryDiff > 0) {
                        deliverySurchargeNotice = `
                            <div style="background: var(--accent-yellow); padding: 16px; border-radius: 12px; margin-bottom: 20px; border-left: 4px solid #f39c12;">
                                <h4 style="margin: 0 0 8px 0; color: var(--text-primary);">⚠️ Требуется доплата за доставку</h4>
                                <p style="margin: 0; color: var(--text-secondary); font-size: 14px;">
                                    Стоимость доставки: <strong>${Pricing.formatPriceWithGBP(paidDeliveryPrice)}</strong> → <strong>${Pricing.formatPriceWithGBP(currentDeliveryPrice)}</strong><br>
                                    Требуется доплата: <strong>${Pricing.formatPriceWithGBP(deliveryDiff)}</strong>
                                </p>
                            </div>
                        `;
                    } else {
                        deliverySurchargeNotice = `
                            <div style="background: var(--accent-green); padding: 16px; border-radius: 12px; margin-bottom: 20px; border-left: 4px solid var(--status-paid);">
                                <h4 style="margin: 0 0 8px 0; color: var(--text-primary);">✅ Доставка стала дешевле</h4>
                                <p style="margin: 0; color: var(--text-secondary); font-size: 14px;">
                                    Стоимость доставки: <strong>${Pricing.formatPriceWithGBP(paidDeliveryPrice)}</strong> → <strong>${Pricing.formatPriceWithGBP(currentDeliveryPrice)}</strong><br>
                                    Будет вычтено из стоимости товара: <strong>${Pricing.formatPriceWithGBP(Math.abs(deliveryDiff))}</strong>
                                </p>
                            </div>
                        `;
                    }
                }
                
                let deliveryInfo = '';
                if (order.includesDelivery) {
                    const displayDeliveryPrice = order.deliveryPriceKopecks || currentDeliveryPrice;
                    if (order.isDeliveryPaid) {
                        let deliveryText = `Оплачено: <strong>${Pricing.formatPriceWithGBP(paidDeliveryPrice)}</strong>`;
                        if (deliveryDiff !== 0) {
                            deliveryText += `<br>Текущая: <strong>${Pricing.formatPriceWithGBP(currentDeliveryPrice)}</strong>`;
                        }
                        deliveryInfo = `
                            <div style="background: var(--accent-green); padding: 16px; border-radius: 12px; margin-bottom: 20px; border-left: 4px solid var(--status-paid);">
                                <h4 style="margin: 0 0 8px 0; color: var(--text-primary);">📦 Доставка</h4>
                                <p style="margin: 0; color: var(--text-secondary); font-size: 14px;">
                                    ${deliveryText}<br>
                                    Статус: <span style="color: var(--status-paid); font-weight: 600;">✅ Оплачена</span>
                                </p>
                            </div>
                        `;
                    } else {
                        deliveryInfo = `
                            <div style="background: var(--accent-yellow); padding: 16px; border-radius: 12px; margin-bottom: 20px; border-left: 4px solid #f39c12;">
                                <h4 style="margin: 0 0 8px 0; color: var(--text-primary);">📦 Доставка</h4>
                                <p style="margin: 0; color: var(--text-secondary); font-size: 14px;">
                                    Стоимость: <strong>${Pricing.formatPriceWithGBP(displayDeliveryPrice)}</strong><br>
                                    Статус: <span style="color: #e74c3c; font-weight: 600;">⚠️ Не оплачена</span>
                                </p>
                            </div>
                        `;
                    }
                }
                
                container.innerHTML = `
                    <div style="background: var(--accent-green); padding: 20px; border-radius: 12px; margin-bottom: 20px; border-left: 4px solid var(--status-paid);">
                        <h3 style="margin: 0 0 8px 0; color: var(--text-primary);">✅ Ваш заказ #${Orders.getOrderNumber(order.id)} оформлен!</h3>
                        <p style="margin: 0; color: var(--text-secondary); font-size: 14px;">
                            Дата: ${order.date}<br>
                            Итого: ${totalPrice}
                            ${isShipped ? '<br><strong>⚠️ Заказ отправлен - изменения невозможны</strong>' : ''}
                        </p>
                    </div>
                    ${deliverySurchargeNotice}
                    ${deliveryInfo}
                    ${itemsHtml}
                    ${Templates.clientCommentsSection(order)}
                    <div style="text-align: center; margin-top: 20px; display: flex; gap: 12px; justify-content: center;">
                        <button class="add-to-order-btn" data-action="createNewOrder">Создать новый заказ</button>
                    </div>
                `;
                updateContactFormVisibility();
                return;
            }
        }
        
        // Иначе показываем форму заказа
        if (!session.orderItems || session.orderItems.length === 0) {
            container.innerHTML = `
                <div class="empty-state">
                    <div class="icon">🧺</div>
                    <h3>Ваш заказ пока пуст</h3>
                    <p>Добавьте пряжу, чтобы начать</p>
                </div>
            `;
            updateContactFormVisibility();
            return;
        }
        
        const totalWeight = session.orderItems.reduce((sum, item) => sum + parseInt(item.grams), 0);
        const deliveryPriceKopecks = Pricing.calculateDeliveryPrice(totalWeight);
        // 7. Используем totalMinor если есть, иначе priceKopecks
        const itemsTotalKopecks = session.orderItems.reduce((sum, item) => {
            const itemPrice = item.totalMinor !== undefined ? item.totalMinor : (item.priceKopecks || 0);
            return sum + itemPrice;
        }, 0);
        
        let weightClass = '';
        if (totalWeight >= 1500) weightClass = 'danger';
        else if (totalWeight >= 1200) weightClass = 'warning';
        
        const itemsTotalDisplay = Pricing.fromMinor(itemsTotalKopecks);
        const deliveryDisplay = Pricing.fromMinor(deliveryPriceKopecks);
        
        container.innerHTML = `
            <div class="order-weight-info ${weightClass}">
                <div style="width: 100%;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                        <div>
                            <span class="weight-label">Общий вес:</span>
                            <span class="weight-value ${weightClass}">${totalWeight} г / 1800 г</span>
                        </div>
                        <div style="text-align: right;">
                            <div><span class="weight-label">Товары:</span> ${itemsTotalDisplay} ₽</div>
                            <div><span class="weight-label">Доставка:</span> ${deliveryDisplay} ₽</div>
                        </div>
                    </div>
                    <div class="weight-bar">
                        <div class="weight-bar-fill ${weightClass}" style="width: ${Math.min(totalWeight / 18, 100)}%"></div>
                    </div>
                </div>
            </div>
            ${session.orderItems.map(item => Templates.orderItem(item)).join('')}
            <div class="payment-buttons">
                <button class="add-to-order-btn" data-action="scrollToAdd" aria-label="Прокрутить к форме добавления товара" title="Добавить ещё товары">Добавить к этому заказу</button>
                <button class="payment-btn delivery" data-action="submitOrder" data-delivery="true" aria-label="Оформить заказ с доставкой" title="Доставка почтой">Оплатить</button>
            </div>
        `;
        
        // Обновляем видимость формы контактов и состояние кнопки добавления
        updateContactFormVisibility();
    }

    function renderSellerOrders() {
        const allOrders = State.getOrders(); // Полный список для вычисления номеров
        let orders = [...allOrders]; // Копия для фильтрации
        const container = document.getElementById('sellerOrdersList');

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            orders = orders.filter(order => 
                order.customer.name.toLowerCase().includes(query) ||
                order.customer.email.toLowerCase().includes(query)
            );
        }

        // Apply status filter
        if (currentFilter !== 'all') {
            if (currentFilter === 'unpaid') {
                // Filter orders with unpaid items
                orders = orders.filter(order => Pricing.calculateTotalToPay(order) > 0);
            } else {
                // Filter by item status
                orders = orders.filter(order => 
                    order.items.some(item => 
                        item.statuses && item.statuses.includes(currentFilter)
                    )
                );
            }
        }

        if (orders.length === 0) {
            let message = 'Заказов пока нет';
            let description = 'Новые заказы будут появляться здесь';
    
            if (searchQuery || currentFilter !== 'all') {
                message = 'Ничего не найдено';
                description = 'Попробуйте изменить параметры поиска или фильтры';
            }
            
            container.innerHTML = `
                <div class="empty-state">
                    <div class="icon">📦</div>
                    <h3>${message}</h3>
                    <p>${description}</p>
                </div>
            `;
            return;
        }

        // Для каждого заказа вычисляем его глобальный номер
        // Самый старый (последний в массиве) = №1, самый новый (первый в массиве) = №N
        const totalOrders = allOrders.length;
        container.innerHTML = orders.map(order => {
            const globalIndex = allOrders.findIndex(o => o.id === order.id);
            const orderNumber = totalOrders - globalIndex; // Обратная нумерация
            return Templates.sellerOrder(order, orderNumber);
        }).join('');
    }
    
    
    function renderChangeLog() {
        const logs = Log.getRecentChanges(50);
        const container = document.getElementById('changeLogList');
        
        if (logs.length === 0) {
            container.innerHTML = `
                <div class="empty-state">
                    <div class="icon">📝</div>
                    <h3>Изменений пока нет</h3>
                </div>
            `;
            return;
        }
        
        container.innerHTML = logs.map(log => Templates.changeLogEntry(log)).join('');
    }
    
    function renderYarnPrices() {
        const state = State.get();
        const container = document.getElementById('yarnPricesGrid');
        
        if (!container) return;
        
        const yarnNames = {
            'feodosiia-230': 'Feodosiia 230m / 50g',
            'feodosiia-350': 'Feodosiia 350m / 50g',
            'feodosiia-cloud-350': 'Feodosiia Cloud 350m / 50g',
            'feodosiia-cloud-175': 'Feodosiia Cloud 175m / 50g',
            'todd-duncan-cashmere': 'Todd & Duncan 100% Cashmere 700m / 50g'
        };
        
        container.innerHTML = Object.entries(state.yarnPrices || {}).map(([yarnId, priceKopecks]) => {
            const priceRubles = Pricing.fromMinor(priceKopecks);
            return `
                <div class="yarn-price-item">
                    <div class="yarn-price-name">${yarnNames[yarnId] || yarnId}</div>
                    <input type="number" 
                           class="yarn-price-input" 
                           id="price-${yarnId}"
                           value="${priceRubles}" 
                           step="0.01" 
                           min="0"
                           placeholder="0.00">
                    <button class="yarn-price-save-btn" 
                            data-action="saveYarnPrice" 
                            data-yarn-id="${yarnId}">
                        💾 Сохранить
                    </button>
                </div>
            `;
        }).join('');
    }
    // ============================================================================
// CLIENT AUTHENTICATION
// ============================================================================
const ClientAuth = (function() {
    const API_URL = 'https://white-waterfall-433d.finestyarns.workers.dev';
    
    // Получить токен из localStorage
    function getToken() {
        return localStorage.getItem('client_token');
    }
    
    // Сохранить токен
    function saveToken(token) {
        localStorage.setItem('client_token', token);
    }
    
    // Удалить токен
    function clearToken() {
        localStorage.removeItem('client_token');
    }
    
    // Проверить авторизован ли пользователь
    async function checkAuth() {
        const token = getToken();
        
        if (!token) {
            showAuthInterface();
            return false;
        }
        
        // Проверяем токен на сервере
        try {
            const response = await fetch(`${API_URL}/api/auth/validate`, {
                headers: {
                    'authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ User authenticated:', data.user);
                showClientInterface(data.user);
                return true;
            } else {
                console.log('❌ Token invalid');
                clearToken();
                showAuthInterface();
                return false;
            }
        } catch (error) {
            console.error('Auth check error:', error);
            showAuthInterface();
            return false;
        }
    }
    
    // Показать формы авторизации
    function showAuthInterface() {
        const authInterface = document.getElementById('authInterface');
        const clientInterface = document.getElementById('clientInterface');
        
        if (authInterface) authInterface.style.display = 'block';
        if (clientInterface) clientInterface.style.display = 'none';
        
        setupAuthTabs();
        setupAuthForms();
    }
    
    // Показать интерфейс клиента
    function showClientInterface(user) {
        const authInterface = document.getElementById('authInterface');
        const clientInterface = document.getElementById('clientInterface');
        
        if (authInterface) authInterface.style.display = 'none';
        if (clientInterface) clientInterface.style.display = 'block';
        
        // Заполняем контактную форму данными пользователя
        fillContactForm(user);
    }
    
    // Заполнить контактную форму
    function fillContactForm(user) {
        const form = document.getElementById('contactForm');
        if (!form) return;
        
        const nameParts = user.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        form.querySelector('[name="firstName"]').value = firstName;
        form.querySelector('[name="lastName"]').value = lastName;
        form.querySelector('[name="email"]').value = user.email;
        form.querySelector('[name="phone"]').value = user.phone || '';
        form.querySelector('[name="address"]').value = user.address || '';
    }
    
    // Настроить переключение вкладок
    function setupAuthTabs() {
        const loginTab = document.getElementById('loginTab');
        const registerTab = document.getElementById('registerTab');
        const loginForm = document.getElementById('loginForm');
        const registerForm = document.getElementById('registerForm');
        
        if (!loginTab || !registerTab) return;
        
        loginTab.onclick = function() {
            loginTab.classList.add('active');
            registerTab.classList.remove('active');
            loginTab.style.borderBottom = '3px solid #8B7355';
            loginTab.style.color = '#8B7355';
            registerTab.style.borderBottom = 'none';
            registerTab.style.color = '#999';
            loginForm.style.display = 'block';
            registerForm.style.display = 'none';
        };
        
        registerTab.onclick = function() {
            registerTab.classList.add('active');
            loginTab.classList.remove('active');
            registerTab.style.borderBottom = '3px solid #8B7355';
            registerTab.style.color = '#8B7355';
            loginTab.style.borderBottom = 'none';
            loginTab.style.color = '#999';
            registerForm.style.display = 'block';
            loginForm.style.display = 'none';
        };
    }
    
    // Настроить формы
    function setupAuthForms() {
        const loginFormElement = document.getElementById('loginFormElement');
        const registerFormElement = document.getElementById('registerFormElement');
        
        if (loginFormElement) {
            loginFormElement.onsubmit = async function(e) {
                e.preventDefault();
                await handleLogin();
            };
        }
        
        if (registerFormElement) {
            registerFormElement.onsubmit = async function(e) {
                e.preventDefault();
                await handleRegister();
            };
        }
    }
    
    // Обработка логина
    async function handleLogin() {
        const email = document.getElementById('loginEmail').value;
        const password = document.getElementById('loginPassword').value;
        const errorDiv = document.getElementById('loginError');
        const submitBtn = document.querySelector('#loginFormElement button[type="submit"]');
        
        errorDiv.style.display = 'none';
        submitBtn.disabled = true;
        submitBtn.textContent = 'Вход...';
        
        try {
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                saveToken(data.token);
                console.log('✅ Login successful');
                showClientInterface(data.user);
            } else {
                errorDiv.textContent = data.error || 'Ошибка входа';
                errorDiv.style.display = 'block';
            }
        } catch (error) {
            console.error('Login error:', error);
            errorDiv.textContent = 'Ошибка соединения с сервером';
            errorDiv.style.display = 'block';
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Войти';
        }
    }
    
    // Обработка регистрации
    async function handleRegister() {
        const name = document.getElementById('registerName').value + ' ' + (document.getElementById('registerLastName').value || '');
        const email = document.getElementById('registerEmail').value;
        const phone = document.getElementById('registerPhone').value;
        const address = document.getElementById('registerAddress').value;
        const password = document.getElementById('registerPassword').value;
        const errorDiv = document.getElementById('registerError');
        const submitBtn = document.querySelector('#registerFormElement button[type="submit"]');
        
        errorDiv.style.display = 'none';
        
        if (password.length < 6) {
            errorDiv.textContent = 'Пароль должен быть минимум 6 символов';
            errorDiv.style.display = 'block';
            return;
        }
        
        submitBtn.disabled = true;
        submitBtn.textContent = 'Регистрация...';
        
        try {
            const response = await fetch(`${API_URL}/api/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name, email, phone, address, password })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                saveToken(data.token);
                console.log('✅ Registration successful');
                showClientInterface(data.user);
            } else {
                errorDiv.textContent = data.error || 'Ошибка регистрации';
                errorDiv.style.display = 'block';
            }
        } catch (error) {
            console.error('Registration error:', error);
            errorDiv.textContent = 'Ошибка соединения с сервером';
            errorDiv.style.display = 'block';
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Зарегистрироваться';
        }
    }
    
    return {
        checkAuth,
        getToken
    };
})();
// ============================================================================
// SELLER AUTHENTICATION
// ============================================================================
const SellerAuth = (function() {
    const API_URL = 'https://white-waterfall-433d.finestyarns.workers.dev';
    
    // Проверить есть ли сохранённая сессия продавца
    function checkSession() {
        return localStorage.getItem('seller_session') === 'ok';
    }
    
    // Сохранить сессию
    function saveSession() {
        localStorage.setItem('seller_session', 'ok');
    }
    
    // Удалить сессию
    function clearSession() {
        localStorage.removeItem('seller_session');
    }
    
    // Проверить PIN и авторизовать
    async function checkAuth() {
        // Если уже есть сессия - показываем интерфейс
        if (checkSession()) {
            console.log('✅ Seller session exists');
            showSellerInterface();
            return true;
        }
        
        // Иначе запрашиваем PIN
        return await requestPIN();
    }
    
    // Запросить PIN у продавца
    async function requestPIN() {
        const pin = prompt('🔐 Введите PIN-код продавца:');
        
        if (!pin) {
            // Пользователь отменил - перенаправляем на главную
            alert('Доступ запрещён');
            window.location.href = '/';
            return false;
        }
        
        // Проверяем PIN на сервере
        try {
            const response = await fetch(`${API_URL}/seller-login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ pin })
            });
            
            const data = await response.json();
            
            if (data.ok) {
                console.log('✅ Seller PIN correct');
                saveSession();
                showSellerInterface();
                return true;
            } else {
                alert('❌ Неверный PIN-код');
                return await requestPIN(); // Спрашиваем снова
            }
        } catch (error) {
            console.error('Seller auth error:', error);
            alert('Ошибка соединения с сервером');
            window.location.href = '/';
            return false;
        }
    }
    
    // Показать интерфейс продавца
    function showSellerInterface() {
        const sellerInterface = document.getElementById('sellerInterface');
        const currencyBtn = document.getElementById('currencyBtn');
        
        if (sellerInterface) sellerInterface.style.display = 'block';
        if (currencyBtn) currencyBtn.style.display = 'flex';
        
        renderSellerOrders();
        renderChangeLog();
        renderYarnPrices();
        renderNotifications();
        updateCurrencyMicrocopy();
    }
    
    // Выход из системы
    function logout() {
        clearSession();
        alert('Вы вышли из системы');
        window.location.reload();
    }
    
    return {
        checkAuth,
        logout
    };
})();
    // ========== Mode Switching ==========
    
    function showClientMode() {
    // Сохраняем текущий режим
    State.setState({ currentMode: 'client' });
    
    // Проверяем авторизацию
    ClientAuth.checkAuth();
}
    
    function showSellerMode() {
    // Сохраняем текущий режим
    State.setState({ currentMode: 'seller' });
    
    // Проверяем авторизацию продавца
    SellerAuth.checkAuth();
}
    
    function renderNotifications() {
        const orders = State.getOrders();
        const container = document.getElementById('notificationsList');
        
        if (!container) return;
        
        // Собираем уведомления на основе требуемых действий
        const notifications = [];
        
        orders.forEach(order => {
            const unpaidItems = order.items.filter(item => !item.isPaid).length;
            const unpaidDelivery = order.withDelivery && !order.isDeliveryPaid;
            const noStatusItems = order.items.filter(item => !item.statuses || item.statuses.length === 0).length;
            
            if (unpaidItems > 0) {
                notifications.push({
                    orderId: order.id,
                    customerName: order.customer.name,
                    type: 'payment',
                    message: `${unpaidItems} неоплаченных позиций`
                });
            }
            
            if (unpaidDelivery) {
                notifications.push({
                    orderId: order.id,
                    customerName: order.customer.name,
                    type: 'payment',
                    message: `Доставка не оплачена`
                });
            }
            
            if (noStatusItems > 0) {
                notifications.push({
                    orderId: order.id,
                    customerName: order.customer.name,
                    type: 'status',
                    message: `${noStatusItems} позиций без статуса`
                });
            }
        });
        
        if (notifications.length === 0) {
            container.innerHTML = `
                <div class="empty-state" style="padding: 20px; text-align: center; color: #999;">
                    <div style="font-size: 32px; margin-bottom: 8px;">✓</div>
                    <div>Всё в порядке!</div>
                </div>
            `;
            return;
        }
        
        container.innerHTML = notifications.map(n => `
            <div class="notification-item" data-action="scrollToOrder" data-order-id="${n.orderId}">
                <div class="notification-icon">${n.type === 'payment' ? '💰' : '📋'}</div>
                <div class="notification-content">
                    <div class="notification-title">Заказ #${Orders.getOrderNumber(n.orderId)} — ${n.customerName}</div>
                    <div class="notification-message">${n.message}</div>
                </div>
            </div>
        `).join('');
    }
    
    // ========== Modal Functions ==========
    
    function showCurrencyModal() {
        const modal = document.getElementById('currencyModal');
        const input = document.getElementById('exchangeRateInput');
        const sourceInput = document.getElementById('rateSourceInput');
        const currency = State.getCurrency();
        
        if (currency.exchangeRateMinor) {
            const rateDisplay = (currency.exchangeRateMinor / 100).toFixed(2);
            input.value = rateDisplay;
            document.getElementById('currentRate').textContent = rateDisplay;
        } else {
            input.value = '';
            document.getElementById('currentRate').textContent = 'не установлен';
        }
        
        // Показать источник если есть
        if (currency.rateSource) {
            sourceInput.value = currency.rateSource;
            document.getElementById('currentSource').textContent = currency.rateSource;
            document.getElementById('rateSourceDisplay').style.display = 'block';
        } else {
            sourceInput.value = '';
            document.getElementById('rateSourceDisplay').style.display = 'none';
        }
        
        document.getElementById('lastUpdate').textContent = currency.lastUpdate || 'никогда';
        modal.classList.add('show');
        input.focus();
    }
    
    function hideCurrencyModal() {
        document.getElementById('currencyModal').classList.remove('show');
        document.getElementById('currencyReminder').style.display = 'none';
    }
    
    
    function showAlert(message) {
        alert(message);
    }
    
    // ========== Toggle Functions ==========
    
    function toggleOrderCollapse(orderId) {
        const element = document.getElementById(`order-${orderId}`);
        if (element) element.classList.toggle('collapsed');
    }
    
    function toggleClientOrder() {
        const card = document.getElementById('clientOrderCard');
        const button = card.querySelector('.client-order-toggle-btn');
        const isCollapsed = card.classList.toggle('collapsed');
        
        // Update ARIA attribute
        if (button) {
            button.setAttribute('aria-expanded', !isCollapsed);
        }
    }
    
    function toggleComments(orderId) {
            console.log('🔍 toggleComments вызвана с orderId:', orderId, 'type:', typeof orderId);
        // Сначала проверяем, не свёрнут ли заказ
        const orderElement = document.getElementById(`order-${orderId}`);
        console.log('📦 Order element:', orderElement);
    console.log('📦 Is collapsed:', orderElement?.classList.contains('collapsed'));
    
        if (orderElement && orderElement.classList.contains('collapsed')) {
            // Если заказ свёрнут, разворачиваем его
            orderElement.classList.remove('collapsed');
        }
        
        const content = document.getElementById(`comments-${orderId}`);
        const button = document.querySelector(`[data-action="toggleComments"][data-order-id="${orderId}"]`);
        console.log('💬 Comments content element:', content);
    console.log('🔘 Button element:', button);
    console.log('👁️ Current classes on content:', content?.className);
        
        if (content) {
            content.classList.toggle('open');
        }
        if (button) {
            button.classList.toggle('open');
        }
    }
    
    function setSearchQuery(query) {
        searchQuery = query;
        renderSellerOrders();
    }
    
    function setFilter(filter) {
        currentFilter = filter;
        
        // Update UI filter chips
        document.querySelectorAll('.filter-chip').forEach(chip => {
            chip.classList.remove('active');
        });
        document.querySelector(`[data-filter="${filter}"]`)?.classList.add('active');
        
        renderSellerOrders();
    }
    
    function refreshSellerView() {
        renderSellerOrders();
        renderNotifications();
    }
    
    function updateCurrencyMicrocopy() {
        const currency = State.getCurrency();
        const microcopy = document.getElementById('currencyMicrocopy');
        const rateSpan = document.getElementById('microcopyRate');
        const dateSpan = document.getElementById('microcopyDate');
        
        if (currency.exchangeRateMinor && currency.lastUpdate) {
            const rate = (currency.exchangeRateMinor / 100).toFixed(2);
            // Парсим дату в формате "28.10.2025, 14:30:00" -> "28.10.2025"
            const dateOnly = currency.lastUpdate.split(',')[0];
            
            rateSpan.textContent = rate;
            dateSpan.textContent = dateOnly;
            microcopy.style.display = 'block';
        } else {
            microcopy.style.display = 'none';
        }
    }
    
    function updateContactFormVisibility() {
        const session = State.getClientSession();
        const contactCard = document.querySelector('#clientInterface .card:first-child'); // Карточка с контактной формой
        
        // Если у клиента есть оформленный заказ, скрываем форму контактов
        if (session.currentOrderId && contactCard) {
            contactCard.style.display = 'none';
        } else if (contactCard) {
            contactCard.style.display = 'block';
        }
        
        // Обновляем состояние кнопки добавления и предупреждения (только если видна форма клиента)
        const clientInterface = document.getElementById('clientInterface');
        if (clientInterface && clientInterface.style.display !== 'none') {
            Actions.validateContactForm();
        }
    }
    
    return {
        renderClientOrder,
        renderSellerOrders,
        renderChangeLog,
        renderYarnPrices,
        renderNotifications,
        refreshSellerView,
        showClientMode,
        showSellerMode,
        showCurrencyModal,
        hideCurrencyModal,
        showAlert,
        toggleOrderCollapse,
        toggleClientOrder,
        toggleComments,
        setSearchQuery,
        setFilter,
        updateCurrencyMicrocopy
    };
})();

// ============================================================================
// ACTIONS MODULE - Business logic handlers
// ============================================================================
const Actions = (function() {
    // Email validation regex (RFC 5322 simplified)
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    function validateContactForm() {
        const session = State.getClientSession();
        const addBtn = document.getElementById('addYarnBtn');
        const warning = document.getElementById('contactWarning');
        
        // Если у клиента уже есть оформленный заказ, разрешаем добавлять товары без проверки формы
        if (session.currentOrderId) {
            if (addBtn) addBtn.disabled = false;
            if (warning) warning.style.display = 'none';
            return true;
        }
        
        // Если в черновике уже есть товары, разрешаем добавлять ещё (форма уже была валидна при добавлении первого)
        if (session.orderItems && session.orderItems.length > 0) {
            if (addBtn) addBtn.disabled = false;
            if (warning) warning.style.display = 'none';
            return true;
        }
        
        // Если заказа нет, проверяем форму для создания нового заказа
        const form = document.getElementById('contactForm');
        if (!form) return false; // Форма не найдена (например, в режиме продавца)
        
        const firstNameInput = form.querySelector('[name="firstName"]');
        const lastNameInput = form.querySelector('[name="lastName"]');
        const addressInput = form.querySelector('[name="address"]');
        const emailInput = form.querySelector('[name="email"]');
        
        if (!firstNameInput || !lastNameInput || !addressInput || !emailInput) {
            return false; // Поля не найдены
        }
        
        const firstName = firstNameInput.value.trim();
        const lastName = lastNameInput.value.trim();
        const address = addressInput.value.trim();
        const email = emailInput.value.trim();
        
        const isValid = firstName && lastName && address && email && EMAIL_REGEX.test(email);
        
        if (isValid) {
            if (addBtn) addBtn.disabled = false;
            if (warning) warning.style.display = 'none';
        } else {
            if (addBtn) addBtn.disabled = true;
            if (warning) warning.style.display = 'block';
        }
        
        return isValid;
    }
    
    function addYarnToOrder() {
        const yarnSelect = document.getElementById('yarnSelect');
        const colorInput = document.getElementById('colorName');
        const gramsInput = document.getElementById('grams');
        
        if (!yarnSelect.value) {
            UI.showAlert('Пожалуйста, выберите пряжу');
            return;
        }
        
        const gramsValue = parseInt(gramsInput.value);
        
        if (!gramsValue || gramsValue < 10) {
            UI.showAlert('Пожалуйста, укажите вес (минимум 10 г)');
            return;
        }
        
        // Проверка кратности 10
        if (gramsValue % 10 !== 0) {
            UI.showAlert('⚠️ Вес должен быть кратен 10 граммам\n\nНапример: 10, 20, 50, 100 г');
            return;
        }
        
        const session = State.getClientSession();
        
        // Если заказ уже оформлен, добавляем товар в реальный заказ
        if (session.currentOrderId) {
            const order = Orders.getOrder(session.currentOrderId);
            if (!order) {
                UI.showAlert('❌ Заказ не найден');
                return;
            }
            
            // Проверяем, не отправлен ли заказ
            const isShipped = order.items.some(item => 
                item.statuses && item.statuses.includes('shipped')
            );
            
            if (isShipped) {
                UI.showAlert('⚠️ Заказ уже отправлен - добавление товаров невозможно');
                return;
            }
            
            const currentWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams), 0);
            
            if (currentWeight + gramsValue > 1800) {
                UI.showAlert('⚠️ Превышен лимит веса!\n\nМаксимальный вес заказа: 1800 г\nТекущий вес: ' + currentWeight + ' г\nВы пытаетесь добавить: ' + gramsValue + ' г');
                return;
            }
            
            const yarnName = yarnSelect.options[yarnSelect.selectedIndex].text;
            const yarnId = yarnSelect.value;
            const colorName = colorInput.value.trim() || 'Без названия';
            
            const pricePerGramKopecks = State.getYarnPrice(yarnId);
            const totalPriceKopecks = pricePerGramKopecks * gramsValue;
            
            // Добавляем новый товар в заказ
            const item = {
                id: Date.now() + Math.random(),
                yarn: yarnName,
                color: colorName,
                grams: gramsValue,
                priceKopecks: totalPriceKopecks,
                totalMinor: totalPriceKopecks,
                unitPriceMinor: pricePerGramKopecks,
                statuses: [],
                isPaid: false
            };
            
            order.items.push(item);
            
            const oldWeight = order.totalWeight || 0;
            order.totalWeight = order.items.reduce((sum, i) => sum + parseInt(i.grams), 0);
            
            // Пересчитываем доставку если есть
            if (order.withDelivery || order.includesDelivery) {
                const newDeliveryPrice = Pricing.calculateDeliveryPrice(order.totalWeight);
                
                // Если доставка уже оплачена, нужно учесть разницу в цене
                if (order.isDeliveryPaid) {
                    const oldDeliveryPrice = order.deliveryPriceKopecks || 0;
                    const deliveryDifference = newDeliveryPrice - oldDeliveryPrice;
                    
                    // Разница будет автоматически учтена в calculateTotalToPay
                    // Обновляем deliveryPriceKopecks на новую цену для правильного отображения
                    order.deliveryPriceKopecks = newDeliveryPrice;
                    
                    console.log(`📦 Доставка пересчитана: ${Pricing.fromMinor(oldDeliveryPrice)}₽ → ${Pricing.fromMinor(newDeliveryPrice)}₽ (разница: ${Pricing.fromMinor(deliveryDifference)}₽)`);
                } else {
                    // Доставка не оплачена - просто обновляем цену
                    order.deliveryPriceKopecks = newDeliveryPrice;
                }
            }
            
            Orders.updateOrder(session.currentOrderId, order);
            Log.addChange(session.currentOrderId, 'item_added', 'Клиент', `${yarnName} - ${colorName} (${gramsValue}г)`);
            
            // Формируем сообщение с информацией о доставке
            let message = `✅ Товар добавлен в заказ!\n\n${yarnName} - ${colorName}\n${gramsValue} г × ${Pricing.fromMinor(pricePerGramKopecks)} ₽/г = ${Pricing.fromMinor(totalPriceKopecks)} ₽`;
            
            // Добавляем информацию об изменении доставки, если она оплачена
            if (order.isDeliveryPaid && (order.withDelivery || order.includesDelivery)) {
                const paidDelivery = order.paidDeliveryPriceKopecks || 0;
                const currentDelivery = Pricing.calculateDeliveryPrice(order.totalWeight);
                const diff = currentDelivery - paidDelivery;
                
                if (diff !== 0) {
                    message += `\n\n📦 Доставка:`;
                    if (diff > 0) {
                        message += `\nТребуется доплата: ${Pricing.fromMinor(diff)} ₽`;
                    } else {
                        message += `\nБудет вычтено: ${Pricing.fromMinor(Math.abs(diff))} ₽`;
                    }
                }
            }
            
            UI.showAlert(message);
            
        } else {
            // Добавляем в черновик заказа (как раньше)
            const currentWeight = session.orderItems.reduce((sum, item) => sum + parseInt(item.grams), 0);
            
            if (currentWeight + gramsValue > 1800) {
                UI.showAlert('⚠️ Превышен лимит веса!\n\nМаксимальный вес заказа: 1800 г\nТекущий вес: ' + currentWeight + ' г\nВы пытаетесь добавить: ' + gramsValue + ' г');
                return;
            }
            
            const yarnName = yarnSelect.options[yarnSelect.selectedIndex].text;
            const yarnId = yarnSelect.value;
            const colorName = colorInput.value.trim() || 'Без названия';
            
            const pricePerGramKopecks = State.getYarnPrice(yarnId);
            const totalPriceKopecks = pricePerGramKopecks * gramsValue;
            
            // Проверка: есть ли уже такая же позиция
            const existingItem = session.orderItems.find(item => 
                item.yarn === yarnName && item.color.toLowerCase() === colorName.toLowerCase()
            );
            
            if (existingItem) {
                existingItem.grams += gramsValue;
                
                if (existingItem.unitPriceMinor && existingItem.unitPriceMinor > 0) {
                    existingItem.totalMinor = existingItem.unitPriceMinor * existingItem.grams;
                    existingItem.priceKopecks = existingItem.totalMinor;
                } else {
                    const pricePerGram = State.getYarnPrice(yarnId);
                    existingItem.unitPriceMinor = pricePerGram;
                    existingItem.totalMinor = pricePerGram * existingItem.grams;
                    existingItem.priceKopecks = existingItem.totalMinor;
                }
                
                UI.showAlert(`✅ Позиции объединены!\n\n${yarnName} - ${colorName}\nОбщий вес: ${existingItem.grams} г\nЦена: ${Pricing.fromMinor(existingItem.totalMinor)} ₽`);
                Log.logItemMerge(0, 'Клиент', yarnName, colorName, existingItem.grams);
            } else {
                const item = {
                    id: Date.now() + Math.random(),
                    yarn: yarnName,
                    color: colorName,
                    grams: gramsValue,
                    priceKopecks: totalPriceKopecks,
                    totalMinor: totalPriceKopecks,
                    unitPriceMinor: pricePerGramKopecks,
                    statuses: []
                };
                session.orderItems.push(item);
                UI.showAlert(`✅ Товар добавлен!\n\n${yarnName} - ${colorName}\n${gramsValue} г × ${Pricing.fromMinor(pricePerGramKopecks)} ₽/г = ${Pricing.fromMinor(totalPriceKopecks)} ₽`);
            }
            
            State.setState({ clientSession: session });
            console.log('Товар добавлен в черновик. Всего товаров:', session.orderItems.length);
            console.log('Товары в сессии:', session.orderItems);
        }
        
        yarnSelect.value = '';
        colorInput.value = '';
        gramsInput.value = '';
        
        UI.renderClientOrder();
    }
    
    function deleteItem(itemId) {
        const session = State.getClientSession();
        
        // Если заказ оформлен, удаляем из реального заказа
        if (session.currentOrderId) {
            const order = Orders.getOrder(session.currentOrderId);
            if (!order) return;
            
            // Проверяем, не отправлен ли заказ
            const isShipped = order.items.some(item => 
                item.statuses && item.statuses.includes('shipped')
            );
            
            if (isShipped) {
                UI.showAlert('⚠️ Заказ уже отправлен - удаление товаров невозможно');
                return;
            }
            
            // Находим товар для удаления
            const itemToDelete = order.items.find(item => item.id == itemId);
            if (!itemToDelete) return;
            
            // Проверяем, не оплачен ли товар
            if (itemToDelete.isPaid) {
                UI.showAlert('⚠️ Товар уже оплачен - удаление невозможно\n\nВы можете только изменить цвет');
                return;
            }
            
            // Удаляем товар
            order.items = order.items.filter(item => item.id != itemId);
            
            const oldWeight = order.totalWeight || 0;
            order.totalWeight = order.items.reduce((sum, i) => sum + parseInt(i.grams), 0);
            
            // Пересчитываем доставку если есть
            if (order.withDelivery || order.includesDelivery) {
                const newDeliveryPrice = Pricing.calculateDeliveryPrice(order.totalWeight);
                
                // Если доставка уже оплачена, нужно учесть разницу в цене
                if (order.isDeliveryPaid) {
                    const oldDeliveryPrice = order.deliveryPriceKopecks || 0;
                    const deliveryDifference = newDeliveryPrice - oldDeliveryPrice;
                    
                    // Разница будет автоматически учтена в calculateTotalToPay
                    // Обновляем deliveryPriceKopecks на новую цену для правильного отображения
                    order.deliveryPriceKopecks = newDeliveryPrice;
                    
                    console.log(`📦 Доставка пересчитана: ${Pricing.fromMinor(oldDeliveryPrice)}₽ → ${Pricing.fromMinor(newDeliveryPrice)}₽ (разница: ${Pricing.fromMinor(deliveryDifference)}₽)`);
                } else {
                    // Доставка не оплачена - просто обновляем цену
                    order.deliveryPriceKopecks = newDeliveryPrice;
                }
            }
            
            Orders.updateOrder(session.currentOrderId, order);
            Log.addChange(session.currentOrderId, 'item_removed', 'Клиент', 
                `${itemToDelete.yarn} - ${itemToDelete.color}`);
        } else {
            // Удаляем из черновика
            session.orderItems = session.orderItems.filter(item => item.id != itemId);
            State.setState({ clientSession: session });
        }
        
        UI.renderClientOrder();
    }
    
    function editColor(itemId) {
        const session = State.getClientSession();
        let item = null;
        let isFromOrder = false;
        
        // Ищем товар в оформленном заказе или в черновике
        if (session.currentOrderId) {
            const order = Orders.getOrder(session.currentOrderId);
            if (order) {
                item = order.items.find(i => i.id == itemId);
                isFromOrder = true;
                
                // Проверяем статусы
                if (item && item.statuses) {
                    if (item.statuses.includes('wound') || item.statuses.includes('shipped')) {
                        UI.showAlert('🔒 Нельзя изменить цвет после намотки или отправки');
                        return;
                    }
                }
            }
        } else {
            item = session.orderItems.find(i => i.id == itemId);
        }
        
        if (!item) return;
        
        const newColor = prompt(`Введите новый цвет для ${item.yarn}:`, item.color);
        
        if (newColor && newColor.trim() && newColor.trim() !== item.color) {
            const oldColor = item.color;
            item.color = newColor.trim();
            
            if (isFromOrder) {
                const order = Orders.getOrder(session.currentOrderId);
                Orders.updateOrder(session.currentOrderId, order);
                Log.addChange(session.currentOrderId, 'color_changed', 'Клиент',
                    `${item.yarn}: ${oldColor} → ${newColor.trim()}`);
            } else {
                State.setState({ clientSession: session });
            }
            
            UI.renderClientOrder();
            UI.showAlert(`✅ Цвет изменён!\n\n${item.yarn}\n${oldColor} → ${newColor.trim()}`);
        }
    }
    
    function submitOrder(withDelivery) {
        const form = document.getElementById('contactForm');
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }
        
        const formData = new FormData(form);
        const session = State.getClientSession();
        
        if (session.orderItems.length === 0) {
            UI.showAlert('Добавьте хотя бы один товар в заказ');
            return;
        }
        
        const customer = {
            name: `${formData.get('firstName')} ${formData.get('lastName')}`,
            address: formData.get('address'),
            phone: formData.get('phone'),
            email: formData.get('email')
        };
        
        const order = Orders.createOrder(customer, session.orderItems, withDelivery);
        
        UI.showAlert(`✅ Спасибо за заказ!\n\nЗаказ #${Orders.getOrderNumber(order.id)} успешно оформлен.\n\n📧 Мы вышлем ссылку для оплаты на ваш email:\n${customer.email}`);
        
        // Сохраняем ID заказа и email в сессии клиента
        session.currentOrderId = order.id;
        session.email = customer.email;
        session.orderItems = []; // Очищаем корзину
        State.setState({ clientSession: session });
        
        form.reset();
        UI.renderClientOrder();
    }
    
    function saveExchangeRate() {
        const input = document.getElementById('exchangeRateInput');
        const sourceInput = document.getElementById('rateSourceInput');
        const value = parseFloat(input.value);
        
        if (isNaN(value) || value <= 0) {
            UI.showAlert('❌ Пожалуйста, введите корректный курс валюты');
            return;
        }
        
        const currentDate = new Date();
        State.updateCurrency({
            exchangeRateMinor: Math.round(value * 100),
            lastUpdate: currentDate.toLocaleString('ru-RU'),
            lastRateConfirmAt: Date.now(),
            rateSource: sourceInput.value.trim() || null,
            lastReminderMonth: `${currentDate.getFullYear()}-${currentDate.getMonth()}`
        });
        
        hideCurrencyReminderBanner();
        UI.hideCurrencyModal();
        UI.renderSellerOrders();
        UI.updateCurrencyMicrocopy();
        
        // Log rate update
        Log.logRateUpdate(value, sourceInput.value.trim() || 'не указан');
        
        UI.showAlert(`✅ Курс валюты обновлён!\n\n1 £ = ${value.toFixed(2)} ₽`);
    }
    
    function confirmRate() {
        // Подтверждаем, что текущий курс актуален
        State.updateCurrency({
            lastRateConfirmAt: Date.now()
        });
        hideCurrencyReminderBanner();
        
        // Log rate confirmation
        Log.logRateConfirm();
        
        UI.showAlert('✅ Курс подтверждён как актуальный');
    }
    
    function remindLater() {
        // Откладываем напоминание на 1 день
        const tomorrow = Date.now() - (29 * 24 * 60 * 60 * 1000); // -29 дней = напомнит завтра
        State.updateCurrency({
            lastRateConfirmAt: tomorrow
        });
        hideCurrencyReminderBanner();
    }
    
    function updateRateFromBanner() {
        hideCurrencyReminderBanner();
        UI.showCurrencyModal();
    }
    
    
    function updatePrice(orderId, itemIndex, price) {
        Payments.updateItemPrice(orderId, itemIndex, price);
        UI.refreshSellerView();
    }
    
    function togglePayment(orderId, itemIndex, type, checked) {
        if (type === 'delivery') {
            Payments.toggleDeliveryPayment(orderId, checked);
        } else {
            Payments.toggleItemPayment(orderId, itemIndex, checked);
        }
        UI.refreshSellerView();
    }
    
    function setItemStatus(orderId, itemIndex, status) {
        console.log('setItemStatus вызвана:', { orderId, itemIndex, status });
        
        const order = Orders.getOrder(orderId);
        console.log('Заказ найден:', order);
        
        if (!order || !order.items[itemIndex]) {
            console.error('Заказ или товар не найден!');
            return;
        }
        
        const item = order.items[itemIndex];
        console.log('Товар до изменения:', { statuses: item.statuses });
        
        if (!item.statuses) item.statuses = [];
        
        const index = item.statuses.indexOf(status);
        if (index > -1) {
            item.statuses.splice(index, 1);
            console.log('Статус удалён');
        } else {
            item.statuses.push(status);
            console.log('Статус добавлен');
        }
        
        console.log('Товар после изменения:', { statuses: item.statuses });
        
        Orders.updateOrder(orderId, order);
        console.log('Заказ обновлён');
        
        Log.addChange(orderId, 'status_change', order.customer.name, 
            `Статус ${item.yarn}: ${status}`);
        UI.refreshSellerView();
    }
    
    
    
    function editItem(orderId, itemIndex) {
        const order = Orders.getOrder(orderId);
        if (!order || !order.items[itemIndex]) return;
        
        const item = order.items[itemIndex];
        const isCustom = item.isCustom || item.unitPriceMinor === 0;
        
        const newColor = prompt('Введите новое название цвета:', item.color);
        const newGrams = prompt('Введите новый вес (г):', item.grams);
        
        let newPrice = null;
        if (isCustom) {
            // Для кастомных товаров запрашиваем новую цену
            const currentPrice = Pricing.fromMinor(item.totalMinor || item.priceKopecks);
            const priceInput = prompt('Введите новую цену за весь товар (в рублях):', currentPrice);
            if (priceInput && !isNaN(priceInput) && parseFloat(priceInput) >= 0) {
                newPrice = Pricing.toMinor(parseFloat(priceInput));
            }
        }
        
        let changes = [];
        if (newColor && newColor !== item.color) {
            item.color = newColor.trim();
            changes.push(`Цвет: "${item.color}" → "${newColor}"`);
        }
        if (newGrams && !isNaN(newGrams) && parseInt(newGrams) !== item.grams) {
            const oldGrams = item.grams;
            item.grams = parseInt(newGrams);
            
            // Пересчитываем цену в зависимости от типа товара
            if (isCustom) {
                // Для кастомного товара цена не пересчитывается автоматически при изменении веса
                // Она меняется только если пользователь ввёл новую цену
                if (newPrice !== null) {
                    item.totalMinor = newPrice;
                    item.priceKopecks = newPrice;
                }
            } else {
                // ✅ Для обычных товаров пересчитываем totalMinor если установлена unitPriceMinor
                if (item.unitPriceMinor && item.unitPriceMinor > 0) {
                    item.totalMinor = item.unitPriceMinor * item.grams;
                    item.priceKopecks = item.totalMinor;
                }
            }
            
            changes.push(`Вес: ${oldGrams}г → ${newGrams}г`);
            
            // Пересчитываем общий вес и доставку
            order.totalWeight = order.items.reduce((sum, i) => sum + parseInt(i.grams || 0), 0);
            
            if (order.withDelivery || order.includesDelivery) {
                const newDeliveryPrice = Pricing.calculateDeliveryPrice(order.totalWeight);
                
                // Если доставка оплачена, обновляем текущую стоимость
                if (order.isDeliveryPaid) {
                    const oldDeliveryPrice = order.deliveryPriceKopecks || 0;
                    order.deliveryPriceKopecks = newDeliveryPrice;
                    
                    const diff = newDeliveryPrice - (order.paidDeliveryPriceKopecks || oldDeliveryPrice);
                    if (diff !== 0) {
                        changes.push(`Доставка: ${Pricing.fromMinor(oldDeliveryPrice)}₽ → ${Pricing.fromMinor(newDeliveryPrice)}₽`);
                    }
                } else {
                    order.deliveryPriceKopecks = newDeliveryPrice;
                }
            }
        }
        
        // Обработка изменения цены для кастомных товаров
        if (isCustom && newPrice !== null && newPrice !== (item.totalMinor || item.priceKopecks)) {
            const oldPrice = Pricing.fromMinor(item.totalMinor || item.priceKopecks);
            const newPriceRub = Pricing.fromMinor(newPrice);
            item.totalMinor = newPrice;
            item.priceKopecks = newPrice;
            changes.push(`Цена: ${oldPrice}₽ → ${newPriceRub}₽`);
        }
        
        if (changes.length > 0) {
            Orders.updateOrder(orderId, order);
            Log.addChange(orderId, 'status_change', order.customer.name, 
                `Товар: ${item.yarn}. ${changes.join(', ')}`);
            UI.renderSellerOrders();
        }
    }
    
    function addOrderItem(orderId) {
        const order = Orders.getOrder(orderId);
        if (!order) {
            UI.showAlert('❌ Заказ не найден');
            return;
        }
        
        // Получаем список пряжи
        const yarnOptions = [
            { id: 'feodosiia-230', name: 'Feodosiia 230m / 50g' },
            { id: 'feodosiia-350', name: 'Feodosiia 350m / 50g' },
            { id: 'feodosiia-cloud-350', name: 'Feodosiia Cloud 350m / 50g' },
            { id: 'feodosiia-cloud-175', name: 'Feodosiia Cloud 175m / 50g' },
            { id: 'todd-duncan-cashmere', name: 'Todd & Duncan 100% Cashmere 700m / 50g' }
        ];
        
        // Формируем текст для prompt
        let optionsText = 'Выберите пряжу (введите номер):\n\n';
        yarnOptions.forEach((yarn, index) => {
            optionsText += `${index + 1}. ${yarn.name}\n`;
        });
        optionsText += `${yarnOptions.length + 1}. Другое (свой товар с ручной ценой)`;
        
        const yarnChoice = prompt(optionsText);
        if (!yarnChoice) return;
        
        const yarnIndex = parseInt(yarnChoice) - 1;
        if (yarnIndex < 0 || yarnIndex > yarnOptions.length) {
            UI.showAlert('❌ Неверный выбор');
            return;
        }
        
        let selectedYarn;
        let isCustomItem = false;
        
        // Проверяем, выбран ли кастомный товар
        if (yarnIndex === yarnOptions.length) {
            // Кастомный товар
            isCustomItem = true;
            const customName = prompt('Введите название товара:');
            if (!customName || !customName.trim()) {
                UI.showAlert('❌ Название товара обязательно');
                return;
            }
            selectedYarn = { id: 'custom', name: customName.trim() };
        } else {
            selectedYarn = yarnOptions[yarnIndex];
        }
        
        const color = prompt('Введите название цвета:');
        if (!color || !color.trim()) {
            UI.showAlert('❌ Название цвета обязательно');
            return;
        }
        
        const grams = prompt('Введите вес (г):');
        if (!grams || isNaN(grams) || parseInt(grams) <= 0) {
            UI.showAlert('❌ Неверный вес');
            return;
        }
        
        const gramsInt = parseInt(grams);
        
        // Проверка кратности 10
        if (gramsInt % 10 !== 0) {
            UI.showAlert('⚠️ Вес должен быть кратен 10 граммам');
            return;
        }
        
        // Проверяем лимит веса
        const currentWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams), 0);
        if (currentWeight + gramsInt > 1800) {
            UI.showAlert(`⚠️ Превышен лимит веса!\n\nМаксимальный вес заказа: 1800 г\nТекущий вес: ${currentWeight} г\nВы пытаетесь добавить: ${gramsInt} г`);
            return;
        }
        
        let totalPriceKopecks;
        let pricePerGramKopecks = 0;
        
        if (isCustomItem) {
            // Для кастомного товара запрашиваем общую цену
            const customPrice = prompt('Введите цену за весь товар (в рублях):');
            if (!customPrice || isNaN(customPrice) || parseFloat(customPrice) < 0) {
                UI.showAlert('❌ Неверная цена');
                return;
            }
            totalPriceKopecks = Pricing.toMinor(parseFloat(customPrice));
            // Для кастомного товара unitPriceMinor не устанавливаем
        } else {
            // Получаем цену пряжи из прайса
            pricePerGramKopecks = State.getYarnPrice(selectedYarn.id);
            totalPriceKopecks = pricePerGramKopecks * gramsInt;
        }
        
        // Создаем новый товар
        const newItem = {
            id: Date.now() + Math.random(),
            yarn: selectedYarn.name,
            color: color.trim(),
            grams: gramsInt,
            priceKopecks: totalPriceKopecks,
            totalMinor: totalPriceKopecks,
            unitPriceMinor: isCustomItem ? 0 : pricePerGramKopecks, // 0 для кастомных товаров
            statuses: [],
            isPaid: false,
            isCustom: isCustomItem // Помечаем кастомный товар
        };
        
        // Добавляем товар в заказ
        order.items.push(newItem);
        
        // Пересчитываем вес и доставку
        order.totalWeight = order.items.reduce((sum, i) => sum + parseInt(i.grams || 0), 0);
        
        if (order.withDelivery || order.includesDelivery) {
            const newDeliveryPrice = Pricing.calculateDeliveryPrice(order.totalWeight);
            
            if (order.isDeliveryPaid) {
                const oldDeliveryPrice = order.deliveryPriceKopecks || 0;
                order.deliveryPriceKopecks = newDeliveryPrice;
                
                console.log(`📦 Доставка пересчитана: ${Pricing.fromMinor(oldDeliveryPrice)}₽ → ${Pricing.fromMinor(newDeliveryPrice)}₽`);
            } else {
                order.deliveryPriceKopecks = newDeliveryPrice;
            }
        }
        
        Orders.updateOrder(orderId, order);
        Log.addChange(orderId, 'item_added', 'Продавец', `${selectedYarn.name} - ${color.trim()} (${gramsInt}г)${isCustomItem ? ' [кастомный товар]' : ''}`);
        
        UI.renderSellerOrders();
        UI.showAlert(`✅ Товар добавлен в заказ!\n\n${selectedYarn.name}\n${color.trim()}\n${gramsInt} г\nЦена: ${Pricing.fromMinor(totalPriceKopecks)} ₽`);
    }
    
    function deleteOrderItem(orderId, itemIndex) {
        const order = Orders.getOrder(orderId);
        if (!order || !order.items[itemIndex]) return;
        
        const item = order.items[itemIndex];
        
        if (!confirm(`Удалить товар из заказа?\n\n${item.yarn}\n${item.color}\n${item.grams} г`)) {
            return;
        }
        
        // Удаляем товар
        order.items.splice(itemIndex, 1);
        
        // Пересчитываем вес и доставку
        order.totalWeight = order.items.reduce((sum, i) => sum + parseInt(i.grams || 0), 0);
        
        if (order.withDelivery || order.includesDelivery) {
            const newDeliveryPrice = Pricing.calculateDeliveryPrice(order.totalWeight);
            
            if (order.isDeliveryPaid) {
                const oldDeliveryPrice = order.deliveryPriceKopecks || 0;
                order.deliveryPriceKopecks = newDeliveryPrice;
            } else {
                order.deliveryPriceKopecks = newDeliveryPrice;
            }
        }
        
        Orders.updateOrder(orderId, order);
        Log.addChange(orderId, 'item_removed', 'Продавец', `${item.yarn} - ${item.color} (${item.grams}г)`);
        
        UI.renderSellerOrders();
        UI.showAlert(`✅ Товар удален из заказа`);
    }
    
    function editTracking(orderId) {
        const order = Orders.getOrder(orderId);
        if (!order) {
            UI.showAlert('❌ Заказ не найден');
            return;
        }
        
        const currentTracking = order.trackingNumber || '';
        const newTracking = prompt('Введите номер отслеживания:', currentTracking);
        
        // Если пользователь нажал "Отмена"
        if (newTracking === null) return;
        
        // Если ввели пустую строку - удаляем номер отслеживания
        if (newTracking.trim() === '') {
            if (currentTracking) {
                order.trackingNumber = '';
                Orders.updateOrder(orderId, order);
                Log.addChange(orderId, 'tracking_updated', 'Продавец', `Номер отслеживания удален (был: ${currentTracking})`);
                UI.renderSellerOrders();
                UI.showAlert('✅ Номер отслеживания удален');
            }
            return;
        }
        
        // Если номер не изменился
        if (newTracking.trim() === currentTracking) return;
        
        // Обновляем номер отслеживания
        const oldTracking = currentTracking || 'не указан';
        order.trackingNumber = newTracking.trim();
        
        Orders.updateOrder(orderId, order);
        Log.addChange(orderId, 'tracking_updated', 'Продавец', `Номер отслеживания: ${oldTracking} → ${newTracking.trim()}`);
        
        UI.renderSellerOrders();
        UI.showAlert(`✅ Номер отслеживания обновлен!\n\n${newTracking.trim()}`);
    }
    
    function addComment(orderId, type) {
        const input = document.getElementById(`${type}-input-${orderId}`);
        const text = input.value.trim();
        
        if (!text) return;
        
        const order = Orders.getOrder(orderId);
        if (!order) return;
        
        // Определяем автора на основе текущего режима
        const state = State.get();
        const author = state.currentMode === 'client' ? 'client' : 'seller';
        
        const comment = {
            text: text,
            author: author,
            time: new Date().toLocaleString('ru-RU')
        };
        
        if (type === 'private') {
            if (!order.privateComments) order.privateComments = [];
            order.privateComments.push(comment);
        } else {
            if (!order.publicComments) order.publicComments = [];
            order.publicComments.push(comment);
        }
        
        Orders.updateOrder(orderId, order);
        input.value = '';
        
        // Перерисовываем интерфейс в зависимости от режима
        if (state.currentMode === 'client') {
            UI.renderClientOrder();
        } else {
            UI.renderSellerOrders();
        }
    }
    
    function switchCommentTab(orderId, tab) {
        const commentsBlock = document.getElementById(`comments-${orderId}`);
        if (!commentsBlock) return;
        
        commentsBlock.querySelectorAll('.comment-tab').forEach(t => 
            t.classList.remove('active'));
        
        // Находим и активируем нужную вкладку
        commentsBlock.querySelectorAll('.comment-tab').forEach(t => {
            if (t.dataset.tab === tab) {
                t.classList.add('active');
            }
        });
        
        document.getElementById(`private-${orderId}`)?.classList.remove('active');
        document.getElementById(`public-${orderId}`)?.classList.remove('active');
        document.getElementById(`${tab}-${orderId}`)?.classList.add('active');
    }
    
    function saveYarnPrice(yarnId) {
        const input = document.getElementById(`price-${yarnId}`);
        const priceRubles = parseFloat(input.value);
        
        if (isNaN(priceRubles) || priceRubles < 0) {
            UI.showAlert('❌ Пожалуйста, введите корректную цену');
            return;
        }
        
        const priceKopecks = Pricing.toMinor(priceRubles);
        const state = State.get();
        
        state.yarnPrices[yarnId] = priceKopecks;
        State.setState(state);
        
        UI.showAlert(`✅ Цена обновлена!\n\n${priceRubles.toFixed(2)} ₽/г`);
        UI.renderYarnPrices();
    }
    
    function createNewOrder() {
        const session = State.getClientSession();
        session.currentOrderId = null;
        session.orderItems = [];
        // Не очищаем email из сессии, но позволяем ввести новый
        State.setState({ clientSession: session });
        
        // Всегда очищаем контактную форму для ввода новых данных
        const form = document.getElementById('contactForm');
        if (form) {
            form.reset();
        }
        
        UI.renderClientOrder();
        
        // Показываем уведомление
        UI.showAlert('📝 Создание нового заказа\n\nЗаполните контактную информацию и добавьте товары');
        
        // Прокручиваем к началу формы (контактная информация)
        const clientInterface = document.getElementById('clientInterface');
        if (clientInterface) {
            clientInterface.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
    
    function applyOverpayment(orderId) {
        const order = Orders.getOrder(orderId);
        if (!order) return;
        
        // Проверяем, есть ли переплата за доставку
        if (!order.includesDelivery || !order.isDeliveryPaid) {
            UI.showAlert('❌ Нет переплаты за доставку');
            return;
        }
        
        const currentWeight = order.items.reduce((sum, item) => sum + parseInt(item.grams || 0), 0);
        const currentDeliveryPrice = Pricing.calculateDeliveryPrice(currentWeight);
        const paidDeliveryPrice = order.paidDeliveryPriceKopecks || order.deliveryPriceKopecks || 0;
        const overpayment = paidDeliveryPrice - currentDeliveryPrice;
        
        if (overpayment <= 0) {
            UI.showAlert('❌ Нет переплаты за доставку');
            return;
        }
        
        // Находим первый неоплаченный товар
        const unpaidItemIndex = order.items.findIndex(item => !item.isPaid);
        if (unpaidItemIndex === -1) {
            UI.showAlert('❌ Все товары уже оплачены');
            return;
        }
        
        const item = order.items[unpaidItemIndex];
        const oldPrice = item.totalMinor || item.priceKopecks || 0;
        const newPrice = Math.max(0, oldPrice - overpayment); // Не может быть меньше 0
        
        // Применяем новую цену
        item.priceKopecks = newPrice;
        item.totalMinor = newPrice;
        if (item.grams && item.grams > 0) {
            item.unitPriceMinor = Math.round(newPrice / item.grams);
        }
        
        // Сохраняем переплату как зачтенную (обнуляем разницу)
        order.paidDeliveryPriceKopecks = currentDeliveryPrice;
        
        Orders.updateOrder(orderId, order);
        
        const appliedAmount = oldPrice - newPrice;
        UI.showAlert(
            `✅ Переплата зачтена!\n\n` +
            `Товар: ${item.yarn} - ${item.color}\n` +
            `Было: ${Pricing.formatPriceWithGBP(oldPrice)}\n` +
            `Зачтено: ${Pricing.formatPriceWithGBP(appliedAmount)}\n` +
            `Стало: ${Pricing.formatPriceWithGBP(newPrice)}`
        );
        
        Log.addChange(orderId, 'price_adjustment', order.customer.name, 
            `Зачтена переплата за доставку ${Pricing.formatPriceWithGBP(appliedAmount)} в товар ${item.yarn}`);
        
        UI.renderSellerOrders();
    }
    
    function switchToOrder(orderId) {
        const session = State.getClientSession();
        session.currentOrderId = Number(orderId);
        session.orderItems = [];
        State.setState({ clientSession: session });
        UI.renderClientOrder();
        
        // Прокрутка к заказу
        const orderList = document.getElementById('orderList');
        if (orderList) {
            orderList.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
    
    // Временное хранилище для черновика заказа продавца
    let sellerOrderDraft = {
        items: []
    };
    
    function showSellerOrderModal() {
        console.log('showSellerOrderModal вызвана');
        const modal = document.getElementById('sellerOrderModal');
        console.log('Модальное окно найдено:', modal);
        modal.style.display = 'flex';
        console.log('Display установлен в flex');
        
        // Очищаем форму
        document.getElementById('sellerOrderFirstName').value = '';
        document.getElementById('sellerOrderLastName').value = '';
        document.getElementById('sellerOrderEmail').value = '';
        document.getElementById('sellerOrderPhone').value = '';
        document.getElementById('sellerOrderAddress').value = '';
        document.getElementById('sellerOrderYarnSelect').value = '';
        document.getElementById('sellerOrderColor').value = '';
        document.getElementById('sellerOrderGrams').value = '';
        
        // Очищаем черновик
        sellerOrderDraft.items = [];
        document.getElementById('sellerOrderItems').style.display = 'none';
    }
    
    function hideSellerOrderModal() {
        const modal = document.getElementById('sellerOrderModal');
        modal.style.display = 'none';
        sellerOrderDraft.items = [];
    }
    
    function addItemToSellerOrder() {
        console.log('addItemToSellerOrder вызвана');
        const yarnSelect = document.getElementById('sellerOrderYarnSelect');
        const colorInput = document.getElementById('sellerOrderColor');
        const gramsInput = document.getElementById('sellerOrderGrams');
        
        console.log('Значения полей:', {
            yarn: yarnSelect?.value,
            color: colorInput?.value,
            grams: gramsInput?.value
        });
        
        if (!yarnSelect.value) {
            UI.showAlert('Пожалуйста, выберите пряжу');
            return;
        }
        
        const gramsValue = parseInt(gramsInput.value);
        if (!gramsValue || gramsValue < 10) {
            UI.showAlert('Пожалуйста, укажите вес (минимум 10 г)');
            return;
        }
        
        if (gramsValue % 10 !== 0) {
            UI.showAlert('⚠️ Вес должен быть кратен 10 граммам');
            return;
        }
        
        const yarnName = yarnSelect.options[yarnSelect.selectedIndex].text;
        const yarnId = yarnSelect.value;
        const colorName = colorInput.value.trim() || 'Без названия';
        const pricePerGramKopecks = State.getYarnPrice(yarnId);
        const totalPriceKopecks = pricePerGramKopecks * gramsValue;
        
        const item = {
            id: Date.now() + Math.random(),
            yarn: yarnName,
            yarnId: yarnId,
            color: colorName,
            grams: gramsValue,
            priceKopecks: totalPriceKopecks,
            totalMinor: totalPriceKopecks,
            unitPriceMinor: pricePerGramKopecks,
            statuses: [],
            isPaid: false
        };
        
        console.log('Добавляем товар:', item);
        sellerOrderDraft.items.push(item);
        console.log('Всего товаров в черновике:', sellerOrderDraft.items.length);
        
        // Очищаем поля товара
        yarnSelect.value = '';
        colorInput.value = '';
        gramsInput.value = '';
        
        renderSellerOrderItems();
    }
    
    function removeSellerOrderItem(index) {
        sellerOrderDraft.items.splice(index, 1);
        renderSellerOrderItems();
    }
    
    function renderSellerOrderItems() {
        const container = document.getElementById('sellerOrderItems');
        const listContainer = document.getElementById('sellerOrderItemsList');
        
        if (sellerOrderDraft.items.length === 0) {
            container.style.display = 'none';
            return;
        }
        
        container.style.display = 'block';
        
        // Рассчитываем общий вес и стоимость
        const totalWeight = sellerOrderDraft.items.reduce((sum, item) => sum + item.grams, 0);
        const totalPrice = sellerOrderDraft.items.reduce((sum, item) => sum + item.totalMinor, 0);
        const deliveryPrice = Pricing.calculateDeliveryPrice(totalWeight);
        
        // Определяем класс для веса
        let weightClass = '';
        if (totalWeight >= 1500) weightClass = 'danger';
        else if (totalWeight >= 1200) weightClass = 'warning';
        
        listContainer.innerHTML = `
            <div class="order-weight-info ${weightClass}" style="margin-bottom: 16px;">
                <div style="width: 100%;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                        <div>
                            <span class="weight-label">Общий вес:</span>
                            <span class="weight-value ${weightClass}">${totalWeight} г / 1800 г</span>
                        </div>
                        <div style="text-align: right;">
                            <div><span class="weight-label">Товары:</span> ${Pricing.fromMinor(totalPrice)} ₽</div>
                            <div><span class="weight-label">Доставка:</span> ${Pricing.fromMinor(deliveryPrice)} ₽</div>
                        </div>
                    </div>
                    <div class="weight-bar">
                        <div class="weight-bar-fill ${weightClass}" style="width: ${Math.min(totalWeight / 18, 100)}%"></div>
                    </div>
                </div>
            </div>
            ${sellerOrderDraft.items.map((item, index) => `
                <div class="order-item" style="margin-bottom: 8px;">
                    <div class="order-item-info">
                        <div class="order-item-name">
                            ${item.yarn}
                            <span class="order-item-color">• ${item.color}</span>
                            <span class="order-item-details">• ${item.grams} г</span>
                            <span class="order-item-details" style="color: #8B7355; font-weight: 600;">• ${Pricing.fromMinor(item.totalMinor)} ₽</span>
                        </div>
                    </div>
                    <button class="delete-btn" data-action="removeSellerOrderItem" data-item-index="${index}">×</button>
                </div>
            `).join('')}
        `;
    }
    
async function saveSellerOrder() {
    console.log('saveSellerOrder вызвана');
    
    // Валидация контактных данных
    const firstName = document.getElementById('sellerOrderFirstName').value.trim();
    const lastName = document.getElementById('sellerOrderLastName').value.trim();
    const email = document.getElementById('sellerOrderEmail').value.trim();
    const phone = document.getElementById('sellerOrderPhone').value.trim();
    const address = document.getElementById('sellerOrderAddress').value.trim();
    
    if (!firstName || !lastName || !email || !address) {
        UI.showAlert('❌ Заполните все обязательные поля');
        return;
    }
    
    if (sellerOrderDraft.items.length === 0) {
        UI.showAlert('❌ Добавьте хотя бы один товар');
        return;
    }
    
    console.log('Валидация пройдена, отправляем заказ на сервер...');
    
    // Подготавливаем данные для отправки
    const totalWeight = sellerOrderDraft.items.reduce((sum, item) => sum + item.grams, 0);
    const deliveryPrice = Pricing.calculateDeliveryPrice(totalWeight);
    
    const orderData = {
        customer: {
            name: `${firstName} ${lastName}`,
            email: email,
            phone: phone || '',
            address: address
        },
        items: JSON.parse(JSON.stringify(sellerOrderDraft.items)),
        totalWeight: totalWeight,
        withDelivery: true,
        deliveryPriceKopecks: deliveryPrice
    };
    
    try {
        // Отправляем на сервер
        const response = await fetch('https://white-waterfall-433d.finestyarns.workers.dev/seller/orders', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Cookie': document.cookie // Передаём cookie с сессией продавца
            },
            credentials: 'include', // Важно для передачи cookies
            body: JSON.stringify(orderData)
        });
        
        if (!response.ok) {
            const error = await response.json();
            UI.showAlert(`❌ Ошибка: ${error.error || 'Не удалось создать заказ'}`);
            return;
        }
        
        const result = await response.json();
        console.log('✅ Заказ создан на сервере:', result);
        
        // Закрываем модальное окно
        hideSellerOrderModal();
        
        // Обновляем интерфейс продавца
        UI.renderSellerOrders();
        UI.renderChangeLog();
        
        UI.showAlert(`✅ Заказ #${result.order.orderNumber} создан!\n\nКлиент: ${firstName} ${lastName}\nТоваров: ${sellerOrderDraft.items.length} шт.\nВес: ${totalWeight} г`);
        
    } catch (error) {
        console.error('Ошибка создания заказа:', error);
        UI.showAlert('❌ Ошибка соединения с сервером');
    }
}
        
    
    
    return {
        validateContactForm,
        addYarnToOrder,
        deleteItem,
        submitOrder,
        saveExchangeRate,
        confirmRate,
        remindLater,
        updateRateFromBanner,
        updatePrice,
        togglePayment,
        setItemStatus,
        editItem,
        addOrderItem,
        deleteOrderItem,
        editTracking,
        editColor,
        addComment,
        switchCommentTab,
        saveYarnPrice,
        createNewOrder,
        switchToOrder,
        applyOverpayment,
        showSellerOrderModal,
        hideSellerOrderModal,
        addItemToSellerOrder,
        saveSellerOrder,
        removeSellerOrderItem
    };
})();

// ============================================================================
// EVENT DELEGATION - Centralized event handling
// ============================================================================
const EventManager = (function() {
    function init() {
        // Delegate all clicks on document
        document.addEventListener('click', handleClick);
        
        // Delegate input changes
        document.addEventListener('change', handleChange);
        
        // Mode toggle (только если кнопки существуют на странице)
const clientModeBtn = document.getElementById('clientMode');
const sellerModeBtn = document.getElementById('sellerMode');

if (clientModeBtn && sellerModeBtn) {
    clientModeBtn.addEventListener('click', function() {
        this.classList.add('active');
        sellerModeBtn.classList.remove('active');
        UI.showClientMode();
    });
    
    sellerModeBtn.addEventListener('click', function() {
        this.classList.add('active');
        clientModeBtn.classList.remove('active');
        UI.showSellerMode();
    });
}

        
        // Phone formatting
        const phoneInput = document.querySelector('[name="phone"]');
        if (phoneInput) {
            phoneInput.addEventListener('input', formatPhone);
        }
        
        // Contact form validation
        const contactForm = document.getElementById('contactForm');
        if (contactForm) {
            const inputs = contactForm.querySelectorAll('input');
            inputs.forEach(input => {
                input.addEventListener('input', Actions.validateContactForm);
                input.addEventListener('blur', Actions.validateContactForm);
            });
            // Initial validation
            Actions.validateContactForm();
        }
        
        // Search and filters
        const searchInput = document.getElementById('orderSearch');
        if (searchInput) {
            searchInput.addEventListener('input', function(e) {
                UI.setSearchQuery(e.target.value);
            });
        }
        
        document.querySelectorAll('.filter-chip').forEach(chip => {
            chip.addEventListener('click', function() {
                const filter = this.getAttribute('data-filter');
                UI.setFilter(filter);
            });
        });
    }
    
    function handleClick(e) {
        const target = e.target.closest('[data-action]');
        if (!target) return;
        
        const action = target.dataset.action;
        console.log('handleClick: action =', action);
        
        // Не используем parseInt для orderId - может быть большое число (timestamp)
        const orderId = target.dataset.orderId ? Number(target.dataset.orderId) : undefined;
        const itemIndex = parseInt(target.dataset.itemIndex);
        const itemId = target.dataset.itemId;
        const status = target.dataset.status;
        const type = target.dataset.type;
        const tab = target.dataset.tab;
        const withDelivery = target.dataset.delivery === 'true';
        const paymentId = parseInt(target.dataset.paymentId);
        
        // Route action
        switch(action) {
            case 'addYarn':
                Actions.addYarnToOrder();
                break;
            case 'deleteItem':
                Actions.deleteItem(itemId);
                break;
            case 'submitOrder':
                Actions.submitOrder(withDelivery);
                break;
            case 'toggleClientOrder':
                UI.toggleClientOrder();
                break;
            case 'toggleOrder':
                UI.toggleOrderCollapse(orderId);
                break;
            case 'setStatus':
                Actions.setItemStatus(orderId, itemIndex, status);
                break;
            case 'editItem':
                Actions.editItem(orderId, itemIndex);
                break;
            case 'addOrderItem':
                Actions.addOrderItem(orderId);
                break;
            case 'deleteOrderItem':
                Actions.deleteOrderItem(orderId, itemIndex);
                break;
            case 'editTracking':
                Actions.editTracking(orderId);
                break;
            case 'toggleComments':
                UI.toggleComments(orderId);
                break;
            case 'switchTab':
                Actions.switchCommentTab(orderId, tab);
                break;
            case 'addComment':
                Actions.addComment(orderId, type);
                break;
            case 'showCurrencyModal':
                UI.showCurrencyModal();
                break;
            case 'hideCurrencyModal':
                UI.hideCurrencyModal();
                break;
            case 'saveExchangeRate':
                Actions.saveExchangeRate();
                break;
            case 'updateRateFromBanner':
                Actions.updateRateFromBanner();
                break;
            case 'remindLater':
                Actions.remindLater();
                break;
            case 'confirmRate':
                Actions.confirmRate();
                break;
            case 'scrollToAdd':
                document.getElementById('yarnSelect').scrollIntoView({ behavior: 'smooth' });
                break;
            case 'saveYarnPrice':
                const yarnId = target.dataset.yarnId;
                Actions.saveYarnPrice(yarnId);
                break;
            case 'scrollToOrder':
                const scrollOrderId = target.dataset.orderId || target.closest('[data-order-id]').dataset.orderId;
                const orderElement = document.getElementById(`order-${scrollOrderId}`);
                if (orderElement) {
                    orderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    orderElement.style.animation = 'highlight 1s ease';
                }
                break;
            case 'createNewOrder':
                Actions.createNewOrder();
                break;
            case 'switchToOrder':
                const switchOrderId = target.dataset.orderId;
                Actions.switchToOrder(switchOrderId);
                break;
            case 'editColor':
                const colorItemId = target.dataset.itemId;
                Actions.editColor(colorItemId);
                break;
            case 'applyOverpayment':
                const overpaymentOrderId = target.dataset.orderId;
                Actions.applyOverpayment(Number(overpaymentOrderId));
                break;
            case 'createSellerOrder':
                Actions.showSellerOrderModal();
                break;
            case 'hideSellerOrderModal':
                Actions.hideSellerOrderModal();
                break;
            case 'addItemToSellerOrder':
                Actions.addItemToSellerOrder();
                break;
            case 'saveSellerOrder':
                Actions.saveSellerOrder();
                break;
            case 'removeSellerOrderItem':
                Actions.removeSellerOrderItem(parseInt(target.dataset.itemIndex));
                break;
        }
    }
    
    function handleChange(e) {
        const target = e.target;
        
        // Price updates
        if (target.dataset.action === 'updatePrice') {
            const orderId = Number(target.dataset.orderId);
            const itemIndex = parseInt(target.dataset.itemIndex);
            Actions.updatePrice(orderId, itemIndex, target.value);
        }
        
        // Payment toggles
        if (target.dataset.action === 'togglePayment') {
            const orderId = Number(target.dataset.orderId);
            const itemIndex = parseInt(target.dataset.itemIndex);
            const type = target.dataset.type;
            Actions.togglePayment(orderId, itemIndex, type, target.checked);
        }
    }
    
    function formatPhone(e) {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 11) value = value.substr(0, 11);
        if (!value.startsWith('7') && value.length > 0) value = '7' + value;
        
        let formatted = '+7';
        if (value.length > 1) formatted += ' (' + value.substr(1, 3);
        if (value.length > 4) formatted += ') ' + value.substr(4, 3);
        if (value.length > 7) formatted += '-' + value.substr(7, 2);
        if (value.length > 9) formatted += '-' + value.substr(9, 2);
        
        e.target.value = formatted;
    }
    
    return { init };
})();

// ============================================================================
// INITIALIZATION
// ============================================================================
function init() {
    console.log('🚀 Initializing Yarn Order Form...');
    
    // Initialize theme
    initTheme();
    console.log('✅ Theme initialized');
    
    // Load data
    Storage.load();
    console.log('✅ Data loaded');
    
    // Setup events
    EventManager.init();
    console.log('✅ Events initialized');
    
    // Show mode based on State
const state = State.get();
console.log('📊 Current mode from State:', state.currentMode);

if (state.currentMode === 'client') {
    console.log('🔵 Showing CLIENT mode');
    UI.showClientMode();
} else {
    console.log('🟢 Showing SELLER mode');
    UI.showSellerMode();
}
    console.log('✅ UI rendered');
    
    // Check monthly reminder
    checkMonthlyReminder();
    console.log('✅ Reminder checked');
    
    console.log('✨ Application ready!');
}

// ============================================================================
// THEME MANAGEMENT - Light theme only
// ============================================================================
function initTheme() {
    // Always use light theme
    document.documentElement.setAttribute('data-theme', 'light');
}

function checkMonthlyReminder() {
    const currency = State.getCurrency();
    const currentTime = Date.now();
    
    // Проверяем: прошло ли более 30 дней с последнего подтверждения
    if (!currency.lastRateConfirmAt) {
        // Если курс вообще не установлен - не показываем напоминание
        if (!currency.exchangeRateMinor) return;
        
        // Если курс установлен, но не было подтверждения - показываем через месяц
        if (currency.lastUpdate) {
            const lastUpdateDate = new Date(currency.lastUpdate);
            const daysSinceUpdate = (currentTime - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysSinceUpdate >= 30) {
                showCurrencyReminderBanner();
            }
        }
    } else {
        // Проверяем прошло ли 30 дней с последнего подтверждения
        const daysSinceConfirm = (currentTime - currency.lastRateConfirmAt) / (1000 * 60 * 60 * 24);
        if (daysSinceConfirm >= 30) {
            showCurrencyReminderBanner();
        }
    }
}

function showCurrencyReminderBanner() {
    const banner = document.getElementById('currencyReminderBanner');
    if (banner) {
        banner.style.display = 'block';
    }
}

function hideCurrencyReminderBanner() {
    const banner = document.getElementById('currencyReminderBanner');
    if (banner) {
        banner.style.display = 'none';
    }
}

// Start application when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Автоматически определяем режим по URL страницы
    const path = window.location.pathname;
    console.log('🔍 Current path:', path);
    
    if (path.includes('seller') || path.includes('продавца')) {
        console.log('✅ Detected SELLER mode');
        State.setState({ currentMode: 'seller' });
    } else {
        console.log('✅ Detected CLIENT mode');
        State.setState({ currentMode: 'client' });
    }
    
    init();
});